import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Bell, X as XIcon, Plus, Trash2, Upload, Palette, Type, Layout, MousePointer, Search, Check, ChevronDown, Home, ChevronRight, Menu, Smartphone, Mail } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { cmsEditorDefaults as defaultBrandSettings } from '../cms/cmsDefaults';

// Extended Google Fonts list - All popular fonts
const GOOGLE_FONTS = [
    // Sans-Serif
    { name: 'Inter', category: 'sans-serif' },
    { name: 'Roboto', category: 'sans-serif' },
    { name: 'Open Sans', category: 'sans-serif' },
    { name: 'Lato', category: 'sans-serif' },
    { name: 'Montserrat', category: 'sans-serif' },
    { name: 'Poppins', category: 'sans-serif' },
    { name: 'Source Sans Pro', category: 'sans-serif' },
    { name: 'Nunito', category: 'sans-serif' },
    { name: 'Nunito Sans', category: 'sans-serif' },
    { name: 'Raleway', category: 'sans-serif' },
    { name: 'Work Sans', category: 'sans-serif' },
    { name: 'DM Sans', category: 'sans-serif' },
    { name: 'Plus Jakarta Sans', category: 'sans-serif' },
    { name: 'Manrope', category: 'sans-serif' },
    { name: 'Outfit', category: 'sans-serif' },
    { name: 'Space Grotesk', category: 'sans-serif' },
    { name: 'Rubik', category: 'sans-serif' },
    { name: 'Quicksand', category: 'sans-serif' },
    { name: 'Cabin', category: 'sans-serif' },
    { name: 'Karla', category: 'sans-serif' },
    { name: 'Mulish', category: 'sans-serif' },
    { name: 'Barlow', category: 'sans-serif' },
    { name: 'Barlow Condensed', category: 'sans-serif' },
    { name: 'Ubuntu', category: 'sans-serif' },
    { name: 'Oswald', category: 'sans-serif' },
    { name: 'Josefin Sans', category: 'sans-serif' },
    { name: 'Exo 2', category: 'sans-serif' },
    { name: 'Archivo', category: 'sans-serif' },
    { name: 'Overpass', category: 'sans-serif' },
    { name: 'Public Sans', category: 'sans-serif' },
    { name: 'IBM Plex Sans', category: 'sans-serif' },
    { name: 'Sora', category: 'sans-serif' },
    { name: 'Lexend', category: 'sans-serif' },
    { name: 'Red Hat Display', category: 'sans-serif' },
    { name: 'Albert Sans', category: 'sans-serif' },
    { name: 'Figtree', category: 'sans-serif' },
    { name: 'Noto Sans', category: 'sans-serif' },
    { name: 'PT Sans', category: 'sans-serif' },
    { name: 'Hind', category: 'sans-serif' },
    { name: 'Cairo', category: 'sans-serif' },
    { name: 'Assistant', category: 'sans-serif' },
    { name: 'Maven Pro', category: 'sans-serif' },
    { name: 'Urbanist', category: 'sans-serif' },
    { name: 'Jost', category: 'sans-serif' },
    { name: 'Bebas Neue', category: 'sans-serif' },
    { name: 'Teko', category: 'sans-serif' },
    { name: 'Titillium Web', category: 'sans-serif' },
    { name: 'Asap', category: 'sans-serif' },
    { name: 'Libre Franklin', category: 'sans-serif' },
    { name: 'Signika', category: 'sans-serif' },
    { name: 'Saira', category: 'sans-serif' },
    { name: 'Catamaran', category: 'sans-serif' },
    // Serif
    { name: 'Playfair Display', category: 'serif' },
    { name: 'Merriweather', category: 'serif' },
    { name: 'Lora', category: 'serif' },
    { name: 'PT Serif', category: 'serif' },
    { name: 'Libre Baskerville', category: 'serif' },
    { name: 'Crimson Text', category: 'serif' },
    { name: 'Cormorant Garamond', category: 'serif' },
    { name: 'EB Garamond', category: 'serif' },
    { name: 'Noto Serif', category: 'serif' },
    { name: 'Source Serif Pro', category: 'serif' },
    { name: 'Bitter', category: 'serif' },
    { name: 'Spectral', category: 'serif' },
    { name: 'Roboto Slab', category: 'serif' },
    { name: 'Arvo', category: 'serif' },
    { name: 'Cardo', category: 'serif' },
    { name: 'Old Standard TT', category: 'serif' },
    { name: 'Vollkorn', category: 'serif' },
    { name: 'DM Serif Display', category: 'serif' },
    { name: 'Fraunces', category: 'serif' },
    { name: 'Literata', category: 'serif' },
    // Display
    { name: 'Anton', category: 'display' },
    { name: 'Pacifico', category: 'display' },
    { name: 'Righteous', category: 'display' },
    { name: 'Alfa Slab One', category: 'display' },
    { name: 'Lobster', category: 'display' },
    { name: 'Permanent Marker', category: 'display' },
    { name: 'Bangers', category: 'display' },
    { name: 'Abril Fatface', category: 'display' },
    // Handwriting
    { name: 'Dancing Script', category: 'handwriting' },
    { name: 'Great Vibes', category: 'handwriting' },
    { name: 'Satisfy', category: 'handwriting' },
    { name: 'Caveat', category: 'handwriting' },
    { name: 'Kalam', category: 'handwriting' },
    // Monospace
    { name: 'JetBrains Mono', category: 'monospace' },
    { name: 'Fira Code', category: 'monospace' },
    { name: 'Source Code Pro', category: 'monospace' },
    { name: 'Roboto Mono', category: 'monospace' },
    { name: 'Space Mono', category: 'monospace' },
    { name: 'IBM Plex Mono', category: 'monospace' },
    { name: 'Ubuntu Mono', category: 'monospace' },
    { name: 'Inconsolata', category: 'monospace' },
];

// Font Selector Component with Custom Font Support
const FontSelector = ({ value, onChange, label, description }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customFont, setCustomFont] = useState('');
    const dropdownRef = useRef(null);

    const isCustomFont = value && !GOOGLE_FONTS.some(f => f.name === value);

    const filteredFonts = useMemo(() => {
        if (!search) return GOOGLE_FONTS;
        return GOOGLE_FONTS.filter(f =>
            f.name.toLowerCase().includes(search.toLowerCase())
        );
    }, [search]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setShowCustomInput(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load font for preview
    useEffect(() => {
        if (value) {
            const fontId = `google-font-preview-${value.replace(/\s+/g, '-').toLowerCase()}`;
            if (!document.getElementById(fontId)) {
                const link = document.createElement('link');
                link.id = fontId;
                link.href = `https://fonts.googleapis.com/css2?family=${value.replace(/ /g, '+')}:wght@300;400;500;600;700;800&display=swap`;
                link.rel = 'stylesheet';
                document.head.appendChild(link);
            }
        }
    }, [value]);

    const handleCustomFontSubmit = () => {
        if (customFont.trim()) {
            onChange(customFont.trim());
            setIsOpen(false);
            setShowCustomInput(false);
            setCustomFont('');
        }
    };

    return (
        <div className="space-y-2">
            <Label>{label}</Label>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 border rounded-md bg-background hover:bg-muted/50 transition-colors"
                >
                    <span style={{ fontFamily: value || 'inherit' }}>
                        {value || 'Select font...'}
                        {isCustomFont && <span className="ml-2 text-xs text-primary">(custom)</span>}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-80 overflow-hidden">
                        <div className="p-2 border-b space-y-2">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search Google Fonts..."
                                    className="w-full pl-8 pr-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary text-[#222224]"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCustomInput(!showCustomInput)}
                                className="w-full text-left text-xs text-primary hover:underline"
                            >
                                {showCustomInput ? '← Back to list' : '+ Enter custom Google Font name'}
                            </button>
                            {showCustomInput && (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={customFont}
                                        onChange={(e) => setCustomFont(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleCustomFontSubmit()}
                                        placeholder="e.g. Roboto Flex"
                                        className="flex-1 px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary text-[#222224]"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleCustomFontSubmit}
                                        className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90"
                                    >
                                        Use
                                    </button>
                                </div>
                            )}
                        </div>
                        {!showCustomInput && (
                            <div className="overflow-y-auto max-h-52">
                                {filteredFonts.map((font) => (
                                    <button
                                        key={font.name}
                                        type="button"
                                        onClick={() => {
                                            onChange(font.name);
                                            setIsOpen(false);
                                            setSearch('');
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/50 ${value === font.name ? 'bg-primary/10' : ''}`}
                                        style={{ fontFamily: font.name }}
                                    >
                                        <span>{font.name}</span>
                                        <span className="text-xs text-muted-foreground">{font.category}</span>
                                        {value === font.name && <Check className="h-4 w-4 text-primary ml-2" />}
                                    </button>
                                ))}
                                {filteredFonts.length === 0 && (
                                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                                        No fonts found. Try entering a custom font name above.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const CMSSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState('brand');
    const logoInputRef = useRef(null);
    const faviconInputRef = useRef(null);
    const headerLogoDefaultRef = useRef(null);
    const headerLogoStickyRef = useRef(null);
    const headerLogoTransparentRef = useRef(null);
    const headerLogoMobileRef = useRef(null);
    const headerLogoLightRef = useRef(null);

    // Brand Settings
    const [brandName, setBrandName] = useState('ESGnet');
    const [brandTagline, setBrandTagline] = useState('Simplifying ESG Compliance');
    const [logoUrl, setLogoUrl] = useState('');
    const [faviconUrl, setFaviconUrl] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#2e7a3b');
    const [secondaryColor, setSecondaryColor] = useState('#399c4a');
    const [fontFamily, setFontFamily] = useState('Inter');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);

    // Contact Information
    const [contactEmail, setContactEmail] = useState('isocore@spout.co.za');
    const [contactPhones, setContactPhones] = useState([
        { label: 'ZM', number: '+260 779024826' },
        { label: 'ZA', number: '+27 68 769 7423' }
    ]);
    const [contactAddressStreet, setContactAddressStreet] = useState('1 Emerald Blvd');
    const [contactAddressArea, setContactAddressArea] = useState('Greenstone Hill');
    const [contactAddressCity, setContactAddressCity] = useState('Edenvale');
    const [contactAddressProvince, setContactAddressProvince] = useState('Gauteng');
    const [contactAddressCountry, setContactAddressCountry] = useState('South Africa');
    const [contactAddressPostalCode, setContactAddressPostalCode] = useState('1609');

    // Layout & Navigation (header, topbar, footer)
    const [topbarEnabled, setTopbarEnabled] = useState(false);
    const [topbarText, setTopbarText] = useState('');
    const [topbarLinkLabel, setTopbarLinkLabel] = useState('');
    const [topbarLinkHref, setTopbarLinkHref] = useState('');
    const [topbarBgColor, setTopbarBgColor] = useState('#2e7a3b');
    const [topbarTextColor, setTopbarTextColor] = useState('#ffffff');
    const [topbarDismissable, setTopbarDismissable] = useState(true);
    const [topbarHeight, setTopbarHeight] = useState('40');
    const [mainNavLinks, setMainNavLinks] = useState(defaultBrandSettings.mainNavLinks || []);
    const [footerLinks, setFooterLinks] = useState(defaultBrandSettings.footerLinks || []);

    // Typography Settings
    const [primaryFont, setPrimaryFont] = useState('Inter');
    const [secondaryFont, setSecondaryFont] = useState('Inter');
    const [headingWeight, setHeadingWeight] = useState('700');
    const [bodyWeight, setBodyWeight] = useState('400');
    const [baseFontSize, setBaseFontSize] = useState('16');
    const [lineHeight, setLineHeight] = useState('1.6');
    const [letterSpacing, setLetterSpacing] = useState('0');
    const [linkUnderline, setLinkUnderline] = useState(false);

    // Topbar Typography
    const [topbarFont, setTopbarFont] = useState('');
    const [topbarFontSize, setTopbarFontSize] = useState('14');
    const [topbarFontWeight, setTopbarFontWeight] = useState('500');

    // Header Typography
    const [headerFont, setHeaderFont] = useState('');
    const [headerNavFontSize, setHeaderNavFontSize] = useState('14');
    const [headerNavFontWeight, setHeaderNavFontWeight] = useState('500');
    const [headerNavLetterSpacing, setHeaderNavLetterSpacing] = useState('0');
    const [headerNavTextTransform, setHeaderNavTextTransform] = useState('none');

    // Header Button Typography
    const [headerButtonFontSize, setHeaderButtonFontSize] = useState('14');
    const [headerButtonFontWeight, setHeaderButtonFontWeight] = useState('500');
    const [headerButtonTextTransform, setHeaderButtonTextTransform] = useState('none');

    // Header Actions
    const [headerActions, setHeaderActions] = useState([]);

    // Mobile Menu Settings (Blocksy-inspired)
    const [mobileMenuPosition, setMobileMenuPosition] = useState('right');
    const [mobileMenuWidth, setMobileMenuWidth] = useState('320');
    const [mobileMenuBgColor, setMobileMenuBgColor] = useState('#ffffff');
    const [mobileMenuTextColor, setMobileMenuTextColor] = useState('#0f172a');
    const [mobileMenuOverlayColor, setMobileMenuOverlayColor] = useState('rgba(0, 0, 0, 0.5)');
    const [mobileMenuOverlayBlur, setMobileMenuOverlayBlur] = useState(true);
    const [mobileMenuShowLogo, setMobileMenuShowLogo] = useState(true);
    const [mobileMenuShowCloseButton, setMobileMenuShowCloseButton] = useState(true);
    const [mobileMenuCloseButtonPosition, setMobileMenuCloseButtonPosition] = useState('right');
    const [mobileMenuAnimation, setMobileMenuAnimation] = useState('slide');
    const [mobileMenuAnimationDuration, setMobileMenuAnimationDuration] = useState('300');
    const [mobileMenuLinkStyle, setMobileMenuLinkStyle] = useState('default');
    const [mobileMenuLinkSpacing, setMobileMenuLinkSpacing] = useState('16');
    const [mobileMenuLinkFontSize, setMobileMenuLinkFontSize] = useState('16');
    const [mobileMenuLinkFontWeight, setMobileMenuLinkFontWeight] = useState('500');
    const [mobileMenuShowDividers, setMobileMenuShowDividers] = useState(true);
    const [mobileMenuDividerColor, setMobileMenuDividerColor] = useState('#e2e8f0');
    const [mobileMenuButtonLayout, setMobileMenuButtonLayout] = useState('stacked');
    const [mobileMenuButtonSpacing, setMobileMenuButtonSpacing] = useState('12');
    const [mobileMenuHeaderHeight, setMobileMenuHeaderHeight] = useState('64');
    const [mobileMenuFooterText, setMobileMenuFooterText] = useState('');
    const [mobileMenuHamburgerStyle, setMobileMenuHamburgerStyle] = useState('default');
    const [mobileMenuHamburgerSize, setMobileMenuHamburgerSize] = useState('24');
    const [mobileMenuHamburgerLineWidth, setMobileMenuHamburgerLineWidth] = useState('2');

    // Footer Typography
    const [footerFont, setFooterFont] = useState('');
    const [footerHeadingFontSize, setFooterHeadingFontSize] = useState('16');
    const [footerHeadingFontWeight, setFooterHeadingFontWeight] = useState('600');
    const [footerLinkFontSize, setFooterLinkFontSize] = useState('14');
    const [footerLinkFontWeight, setFooterLinkFontWeight] = useState('400');

    // Header Settings - Basic
    const [headerBgColor, setHeaderBgColor] = useState('#ffffff');
    const [headerTextColor, setHeaderTextColor] = useState('#111827');
    const [headerHeight, setHeaderHeight] = useState('72');
    const [headerSticky, setHeaderSticky] = useState(true);
    const [headerMobileBreakpoint, setHeaderMobileBreakpoint] = useState('768');

    // Header Settings - Advanced (Multiple Logos)
    const [headerLogoDefault, setHeaderLogoDefault] = useState('');
    const [headerLogoSticky, setHeaderLogoSticky] = useState('');
    const [headerLogoTransparent, setHeaderLogoTransparent] = useState('');
    const [headerLogoMobile, setHeaderLogoMobile] = useState('');
    const [headerLogoLight, setHeaderLogoLight] = useState(''); // White/light logo for dark backgrounds
    const [headerLogoWidth, setHeaderLogoWidth] = useState('150');

    // Logo upload states
    const [uploadingHeaderLogo, setUploadingHeaderLogo] = useState({
        default: false, sticky: false, transparent: false, mobile: false, light: false
    });
    const [uploadProgress, setUploadProgress] = useState({
        default: 0, sticky: 0, transparent: 0, mobile: 0, light: 0
    });

    // Footer background image upload
    const [uploadingFooterBg, setUploadingFooterBg] = useState(false);
    const footerBgInputRef = useRef(null);

    // Header Settings - Transparency & Scroll
    const [headerIsTransparent, setHeaderIsTransparent] = useState(false);
    const [headerTransparentBgColor, setHeaderTransparentBgColor] = useState('rgba(0,0,0,0)');
    const [headerBgColorScrolled, setHeaderBgColorScrolled] = useState('#ffffff');
    const [headerTextColorScrolled, setHeaderTextColorScrolled] = useState('#111827');
    const [headerHeightScrolled, setHeaderHeightScrolled] = useState('56');
    const [headerScrollThreshold, setHeaderScrollThreshold] = useState('50');
    const [headerShowShadowOnScroll, setHeaderShowShadowOnScroll] = useState(true);

    // Header Settings - Link Styling
    const [headerLinkColor, setHeaderLinkColor] = useState('#374151');
    const [headerLinkHoverColor, setHeaderLinkHoverColor] = useState('#2e7a3b');
    const [headerLinkActiveColor, setHeaderLinkActiveColor] = useState('#2e7a3b');

    // Header Settings - Border
    const [headerBorderEnabled, setHeaderBorderEnabled] = useState(true);
    const [headerBorderColor, setHeaderBorderColor] = useState('#e5e7eb');
    const [headerBorderWidth, setHeaderBorderWidth] = useState('1');

    // Footer Settings - Basic
    const [footerBgColor, setFooterBgColor] = useState('#111827');
    const [footerTextColor, setFooterTextColor] = useState('#ffffff');
    const [footerColumns, setFooterColumns] = useState('4');
    const [footerSocialLinks, setFooterSocialLinks] = useState([
        { platform: 'linkedin', url: '' },
        { platform: 'twitter', url: '' },
        { platform: 'facebook', url: '' },
        { platform: 'instagram', url: '' },
        { platform: 'youtube', url: '' }
    ]);

    // Footer Settings - Advanced
    const [footerHeadingColor, setFooterHeadingColor] = useState('#ffffff');
    const [footerLinkColor, setFooterLinkColor] = useState('#9ca3af');
    const [footerLinkHoverColor, setFooterLinkHoverColor] = useState('#2e7a3b');
    const [footerLayout, setFooterLayout] = useState('standard');
    const [footerShowLogo, setFooterShowLogo] = useState(true);
    const [footerShowTagline, setFooterShowTagline] = useState(true);
    const [footerShowSocial, setFooterShowSocial] = useState(true);
    const [footerShowTrustBadges, setFooterShowTrustBadges] = useState(true);
    const [footerCopyrightText, setFooterCopyrightText] = useState('© {year} {brandName}. All rights reserved.');
    const [footerBackgroundImage, setFooterBackgroundImage] = useState('');
    const [footerBackgroundOverlay, setFooterBackgroundOverlay] = useState('rgba(17,24,39,0.95)');

    // Powered By Branding (Footer)
    const [footerShowPoweredBy, setFooterShowPoweredBy] = useState(true);
    const [footerPoweredByText, setFooterPoweredByText] = useState('Powered by Spout Technologies');
    const [footerPoweredByLink, setFooterPoweredByLink] = useState('https://spout.co.za');
    const [footerPoweredByLogoDarkUrl, setFooterPoweredByLogoDarkUrl] = useState('');
    const [footerPoweredByLogoDarkHoverUrl, setFooterPoweredByLogoDarkHoverUrl] = useState('');
    const [footerPoweredByLogoLightUrl, setFooterPoweredByLogoLightUrl] = useState('');
    const [footerPoweredByLogoLightHoverUrl, setFooterPoweredByLogoLightHoverUrl] = useState('');

    // Button Styles
    const [buttonPrimaryBg, setButtonPrimaryBg] = useState('#2e7a3b');
    const [buttonPrimaryText, setButtonPrimaryText] = useState('#ffffff');
    const [buttonPrimaryHoverBg, setButtonPrimaryHoverBg] = useState('#256b32');
    const [buttonSecondaryBg, setButtonSecondaryBg] = useState('#ffffff');
    const [buttonSecondaryText, setButtonSecondaryText] = useState('#2e7a3b');
    const [buttonSecondaryBorder, setButtonSecondaryBorder] = useState('#2e7a3b');
    const [buttonBorderRadius, setButtonBorderRadius] = useState('8');
    const [buttonPaddingX, setButtonPaddingX] = useState('24');
    const [buttonPaddingY, setButtonPaddingY] = useState('12');

    // Social Proof Settings
    const [socialProofEnabled, setSocialProofEnabled] = useState(true);
    const [socialProofInitialDelay, setSocialProofInitialDelay] = useState(15);
    const [socialProofInterval, setSocialProofInterval] = useState(180); // 3 minutes
    const [socialProofDuration, setSocialProofDuration] = useState(6);
    const [socialProofNotifications, setSocialProofNotifications] = useState([]);

    // Exit Intent Settings
    const [exitIntentEnabled, setExitIntentEnabled] = useState(true);
    const [exitIntentCooldown, setExitIntentCooldown] = useState(24);
    const [exitIntentTitle, setExitIntentTitle] = useState('Wait! Before You Go...');
    const [exitIntentSubtitle, setExitIntentSubtitle] = useState('Start your ESG journey with a 14-day free trial — no credit card required.');
    const [exitIntentBenefits, setExitIntentBenefits] = useState([]);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);

            // Load brand settings
            const brandDoc = await getDoc(doc(db, 'cms_global_settings', 'brand-settings'));
            if (brandDoc.exists()) {
                const brandData = brandDoc.data();
                setBrandName(brandData.brandName ?? 'ESGnet');
                setBrandTagline(brandData.brandTagline ?? 'Simplifying ESG Compliance');
                setLogoUrl(brandData.logoUrl ?? '');
                setFaviconUrl(brandData.faviconUrl ?? '');
                setPrimaryColor(brandData.primaryColor ?? '#2e7a3b');
                setSecondaryColor(brandData.secondaryColor ?? '#399c4a');
                setFontFamily(brandData.fontFamily ?? 'Inter');

                // Contact Information
                if (brandData.contact) {
                    setContactEmail(brandData.contact.email ?? 'isocore@spout.co.za');
                    setContactPhones(brandData.contact.phones ?? [
                        { label: 'ZM', number: '+260 779024826' },
                        { label: 'ZA', number: '+27 68 769 7423' }
                    ]);
                    if (brandData.contact.address) {
                        setContactAddressStreet(brandData.contact.address.street ?? '1 Emerald Blvd');
                        setContactAddressArea(brandData.contact.address.area ?? 'Greenstone Hill');
                        setContactAddressCity(brandData.contact.address.city ?? 'Edenvale');
                        setContactAddressProvince(brandData.contact.address.province ?? 'Gauteng');
                        setContactAddressCountry(brandData.contact.address.country ?? 'South Africa');
                        setContactAddressPostalCode(brandData.contact.address.postalCode ?? '1609');
                    }
                }

                const topbar = brandData.topbar || {};
                setTopbarEnabled(topbar.enabled ?? false);
                setTopbarText(topbar.text ?? '');
                setTopbarLinkLabel(topbar.linkLabel ?? '');
                setTopbarLinkHref(topbar.linkHref ?? '');
                setTopbarBgColor(topbar.bgColor ?? '#2e7a3b');
                setTopbarTextColor(topbar.textColor ?? '#ffffff');
                setTopbarDismissable(topbar.dismissable ?? true);
                setTopbarHeight(topbar.height ?? '40');
                setTopbarFont(topbar.font ?? '');
                setTopbarFontSize(topbar.fontSize ?? '14');
                setTopbarFontWeight(topbar.fontWeight ?? '500');

                // Merge nav links - ensure Insights and other defaults are present
                const savedNavLinks = brandData.mainNavLinks || [];
                if (savedNavLinks.length > 0) {
                    // Check if Insights exists, if not add it
                    const hasInsights = savedNavLinks.some(l => l.id === 'insights' || l.href === '/insights');
                    if (!hasInsights) {
                        const insightsLink = defaultBrandSettings.mainNavLinks.find(l => l.id === 'insights');
                        if (insightsLink) {
                            savedNavLinks.push(insightsLink);
                        }
                    }
                    setMainNavLinks(savedNavLinks);
                } else {
                    setMainNavLinks(defaultBrandSettings.mainNavLinks);
                }
                setFooterLinks(
                    brandData.footerLinks && brandData.footerLinks.length
                        ? brandData.footerLinks
                        : defaultBrandSettings.footerLinks
                );

                // Typography
                if (brandData.typography) {
                    setPrimaryFont(brandData.typography.primaryFont ?? 'Inter');
                    setSecondaryFont(brandData.typography.secondaryFont ?? 'Inter');
                    setHeadingWeight(brandData.typography.headingWeight ?? '700');
                    setBodyWeight(brandData.typography.bodyWeight ?? '400');
                    setBaseFontSize(brandData.typography.baseFontSize ?? '16');
                    setLineHeight(brandData.typography.lineHeight ?? '1.6');
                    setLetterSpacing(brandData.typography.letterSpacing ?? '0');
                    setLinkUnderline(brandData.typography.linkUnderline ?? false);
                }

                // Header - Basic
                if (brandData.header) {
                    setHeaderBgColor(brandData.header.bgColor ?? '#ffffff');
                    setHeaderTextColor(brandData.header.textColor ?? '#111827');
                    setHeaderHeight(brandData.header.height ?? '72');
                    setHeaderSticky(brandData.header.sticky ?? true);
                    setHeaderMobileBreakpoint(brandData.header.mobileBreakpoint ?? '768');
                    // Advanced - Multiple Logos
                    setHeaderLogoDefault(brandData.header.logoDefault ?? '');
                    setHeaderLogoSticky(brandData.header.logoSticky ?? '');
                    setHeaderLogoTransparent(brandData.header.logoTransparent ?? '');
                    setHeaderLogoMobile(brandData.header.logoMobile ?? '');
                    setHeaderLogoLight(brandData.header.logoLight ?? '');
                    setHeaderLogoWidth(brandData.header.logoWidth ?? '150');
                    // Advanced - Transparency & Scroll
                    setHeaderIsTransparent(brandData.header.isTransparent ?? false);
                    setHeaderTransparentBgColor(brandData.header.transparentBgColor ?? 'rgba(0,0,0,0)');
                    setHeaderBgColorScrolled(brandData.header.bgColorScrolled ?? '#ffffff');
                    setHeaderTextColorScrolled(brandData.header.textColorScrolled ?? '#111827');
                    setHeaderHeightScrolled(brandData.header.heightScrolled ?? '56');
                    setHeaderScrollThreshold(brandData.header.scrollThreshold ?? '50');
                    setHeaderShowShadowOnScroll(brandData.header.showShadowOnScroll ?? true);
                    // Advanced - Link Styling
                    setHeaderLinkColor(brandData.header.linkColor ?? '#374151');
                    setHeaderLinkHoverColor(brandData.header.linkHoverColor ?? '#2e7a3b');
                    setHeaderLinkActiveColor(brandData.header.linkActiveColor ?? '#2e7a3b');
                    // Advanced - Border
                    setHeaderBorderEnabled(brandData.header.borderEnabled ?? true);
                    setHeaderBorderColor(brandData.header.borderColor ?? '#e5e7eb');
                    setHeaderBorderWidth(brandData.header.borderWidth ?? '1');
                    // Typography
                    setHeaderFont(brandData.header.font ?? '');
                    setHeaderNavFontSize(brandData.header.navFontSize ?? '14');
                    setHeaderNavFontWeight(brandData.header.navFontWeight ?? '500');
                    setHeaderNavLetterSpacing(brandData.header.navLetterSpacing ?? '0');
                    setHeaderNavTextTransform(brandData.header.navTextTransform ?? 'none');
                    // Button Typography
                    setHeaderButtonFontSize(brandData.header.buttonFontSize ?? '14');
                    setHeaderButtonFontWeight(brandData.header.buttonFontWeight ?? '500');
                    setHeaderButtonTextTransform(brandData.header.buttonTextTransform ?? 'none');
                    // Actions
                    if (brandData.header.actions) {
                        setHeaderActions(brandData.header.actions);
                    } else {
                        // Fallback to defaults if not present
                        setHeaderActions(defaultBrandSettings.header.actions);
                    }
                    // Mobile Menu Settings
                    if (brandData.header.mobileMenu) {
                        const mm = brandData.header.mobileMenu;
                        setMobileMenuPosition(mm.position ?? 'right');
                        setMobileMenuWidth(mm.width ?? '320');
                        setMobileMenuBgColor(mm.bgColor ?? '#ffffff');
                        setMobileMenuTextColor(mm.textColor ?? '#0f172a');
                        setMobileMenuOverlayColor(mm.overlayColor ?? 'rgba(0, 0, 0, 0.5)');
                        setMobileMenuOverlayBlur(mm.overlayBlur ?? true);
                        setMobileMenuShowLogo(mm.showLogo ?? true);
                        setMobileMenuShowCloseButton(mm.showCloseButton ?? true);
                        setMobileMenuCloseButtonPosition(mm.closeButtonPosition ?? 'right');
                        setMobileMenuAnimation(mm.animation ?? 'slide');
                        setMobileMenuAnimationDuration(mm.animationDuration ?? '300');
                        setMobileMenuLinkStyle(mm.linkStyle ?? 'default');
                        setMobileMenuLinkSpacing(mm.linkSpacing ?? '16');
                        setMobileMenuLinkFontSize(mm.linkFontSize ?? '16');
                        setMobileMenuLinkFontWeight(mm.linkFontWeight ?? '500');
                        setMobileMenuShowDividers(mm.showDividers ?? true);
                        setMobileMenuDividerColor(mm.dividerColor ?? '#e2e8f0');
                        setMobileMenuButtonLayout(mm.buttonLayout ?? 'stacked');
                        setMobileMenuButtonSpacing(mm.buttonSpacing ?? '12');
                        setMobileMenuHeaderHeight(mm.headerHeight ?? '64');
                        setMobileMenuFooterText(mm.footerText ?? '');
                        setMobileMenuHamburgerStyle(mm.hamburgerStyle ?? 'default');
                        setMobileMenuHamburgerSize(mm.hamburgerSize ?? '24');
                        setMobileMenuHamburgerLineWidth(mm.hamburgerLineWidth ?? '2');
                    }
                }

                // Footer - Basic & Advanced
                if (brandData.footer) {
                    setFooterBgColor(brandData.footer.bgColor ?? '#111827');
                    setFooterTextColor(brandData.footer.textColor ?? '#ffffff');
                    setFooterColumns(brandData.footer.columns ?? '4');
                    setFooterSocialLinks(brandData.footer.socialLinks ?? [
                        { platform: 'linkedin', url: '' },
                        { platform: 'twitter', url: '' },
                        { platform: 'facebook', url: '' },
                        { platform: 'instagram', url: '' },
                        { platform: 'youtube', url: '' }
                    ]);
                    // Advanced
                    setFooterHeadingColor(brandData.footer.headingColor ?? '#ffffff');
                    setFooterLinkColor(brandData.footer.linkColor ?? '#9ca3af');
                    setFooterLinkHoverColor(brandData.footer.linkHoverColor ?? '#2e7a3b');
                    setFooterLayout(brandData.footer.layout ?? 'standard');
                    setFooterShowLogo(brandData.footer.showLogo ?? true);
                    setFooterShowTagline(brandData.footer.showTagline ?? true);
                    setFooterShowSocial(brandData.footer.showSocial ?? true);
                    setFooterShowTrustBadges(brandData.footer.showTrustBadges ?? true);
                    setFooterCopyrightText(brandData.footer.copyrightText ?? '© {year} {brandName}. All rights reserved.');
                    setFooterBackgroundImage(brandData.footer.backgroundImage ?? '');
                    setFooterBackgroundOverlay(brandData.footer.backgroundOverlay ?? 'rgba(17,24,39,0.95)');
                    // Powered By
                    setFooterShowPoweredBy(brandData.footer.showPoweredBy ?? true);
                    setFooterPoweredByText(brandData.footer.poweredByText ?? 'Powered by Spout Technologies');
                    setFooterPoweredByLink(brandData.footer.poweredByLink ?? 'https://spout.co.za');
                    setFooterPoweredByLogoDarkUrl(brandData.footer.poweredByLogoDarkUrl ?? '');
                    setFooterPoweredByLogoDarkHoverUrl(brandData.footer.poweredByLogoDarkHoverUrl ?? '');
                    setFooterPoweredByLogoLightUrl(brandData.footer.poweredByLogoLightUrl ?? '');
                    setFooterPoweredByLogoLightHoverUrl(brandData.footer.poweredByLogoLightHoverUrl ?? '');
                    // Typography
                    setFooterFont(brandData.footer.font ?? '');
                    setFooterHeadingFontSize(brandData.footer.headingFontSize ?? '16');
                    setFooterHeadingFontWeight(brandData.footer.headingFontWeight ?? '600');
                    setFooterLinkFontSize(brandData.footer.linkFontSize ?? '14');
                    setFooterLinkFontWeight(brandData.footer.linkFontWeight ?? '400');
                }

                // Button Styles
                if (brandData.buttonStyles) {
                    setButtonPrimaryBg(brandData.buttonStyles.primaryBg ?? '#2e7a3b');
                    setButtonPrimaryText(brandData.buttonStyles.primaryText ?? '#ffffff');
                    setButtonPrimaryHoverBg(brandData.buttonStyles.primaryHoverBg ?? '#256b32');
                    setButtonSecondaryBg(brandData.buttonStyles.secondaryBg ?? '#ffffff');
                    setButtonSecondaryText(brandData.buttonStyles.secondaryText ?? '#2e7a3b');
                    setButtonSecondaryBorder(brandData.buttonStyles.secondaryBorder ?? '#2e7a3b');
                    setButtonBorderRadius(brandData.buttonStyles.borderRadius ?? '8');
                    setButtonPaddingX(brandData.buttonStyles.paddingX ?? '24');
                    setButtonPaddingY(brandData.buttonStyles.paddingY ?? '12');
                }
            } else {
                // If no brand settings exist yet, fall back to defaults for navigation
                setMainNavLinks(defaultBrandSettings.mainNavLinks);
                setFooterLinks(defaultBrandSettings.footerLinks);
            }

            // Load conversion widget settings
            const settingsDoc = await getDoc(doc(db, 'cms_global_settings', 'conversion-widgets'));
            if (settingsDoc.exists()) {
                const data = settingsDoc.data();

                // Social Proof
                if (data.socialProof) {
                    setSocialProofEnabled(data.socialProof.enabled ?? true);
                    setSocialProofInitialDelay(data.socialProof.initialDelay ?? 15);
                    setSocialProofInterval(data.socialProof.interval ?? 180);
                    setSocialProofDuration(data.socialProof.duration ?? 6);
                    setSocialProofNotifications(data.socialProof.notifications ?? getDefaultNotifications());
                }

                // Exit Intent
                if (data.exitIntent) {
                    setExitIntentEnabled(data.exitIntent.enabled ?? true);
                    setExitIntentCooldown(data.exitIntent.cooldown ?? 24);
                    setExitIntentTitle(data.exitIntent.title ?? 'Wait! Before You Go...');
                    setExitIntentSubtitle(data.exitIntent.subtitle ?? 'Start your ESG journey with a 14-day free trial — no credit card required.');
                    setExitIntentBenefits(data.exitIntent.benefits ?? getDefaultBenefits());
                }
            } else {
                // Set defaults
                setSocialProofNotifications(getDefaultNotifications());
                setExitIntentBenefits(getDefaultBenefits());
            }
        } catch (error) {
            console.error('Error loading settings:', error);
            toast.error('Error loading settings');
        } finally {
            setLoading(false);
        }
    };

    const getDefaultNotifications = () => [
        { id: 1, message: 'A mining company just started their free trial', location: 'Johannesburg, South Africa', time: '2 minutes ago' },
        { id: 2, message: 'A financial services firm upgraded to Enterprise plan', location: 'Cape Town, South Africa', time: '15 minutes ago' },
        { id: 3, message: 'A retail group completed their first ESG report', location: 'Western Cape, South Africa', time: '1 hour ago' },
        { id: 4, message: 'An energy company booked a demo', location: 'Gauteng, South Africa', time: '3 hours ago' },
        { id: 5, message: 'A manufacturing firm downloaded the Carbon Accounting Guide', location: 'Johannesburg, South Africa', time: '5 hours ago' },
        { id: 6, message: 'A consulting firm in Cape Town started their trial', location: 'Cape Town, South Africa', time: '4 hours ago' },
        { id: 7, message: 'A logistics company completed onboarding', location: 'Durban, South Africa', time: '6 hours ago' }
    ];

    const getDefaultBenefits = () => [
        'Full access to all features for 14 days',
        'Free onboarding and training session',
        'Cancel anytime, no questions asked'
    ];

    const handleLogoUpload = async (event, type = 'logo') => {
        const file = event.target.files?.[0];
        if (!file) return;

        const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
        const setUrl = type === 'logo' ? setLogoUrl : setFaviconUrl;

        try {
            setUploading(true);
            const timestamp = Date.now();
            const fileName = `${type}_${timestamp}_${file.name}`;
            const storageRef = ref(storage, `cms-brand/${fileName}`);

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            setUrl(url);
            toast.success(`${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully`);
        } catch (error) {
            console.error(`Error uploading ${type}:`, error);
            toast.error(`Failed to upload ${type}`);
        } finally {
            setUploading(false);
        }
    };

    // Header logo upload handler with progress
    const handleHeaderLogoUpload = async (event, logoType) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const setters = {
            default: setHeaderLogoDefault,
            sticky: setHeaderLogoSticky,
            transparent: setHeaderLogoTransparent,
            mobile: setHeaderLogoMobile,
            light: setHeaderLogoLight
        };

        try {
            setUploadingHeaderLogo(prev => ({ ...prev, [logoType]: true }));
            setUploadProgress(prev => ({ ...prev, [logoType]: 0 }));

            const timestamp = Date.now();
            const fileName = `header_${logoType}_${timestamp}_${file.name}`;
            const storageRef = ref(storage, `cms-brand/logos/${fileName}`);

            // Use uploadBytesResumable for progress tracking
            const { uploadBytesResumable } = await import('firebase/storage');
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setUploadProgress(prev => ({ ...prev, [logoType]: progress }));
                },
                (error) => {
                    console.error(`Error uploading ${logoType} logo:`, error);
                    toast.error(`Failed to upload ${logoType} logo`);
                    setUploadingHeaderLogo(prev => ({ ...prev, [logoType]: false }));
                },
                async () => {
                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                    setters[logoType](url);
                    toast.success(`${logoType.charAt(0).toUpperCase() + logoType.slice(1)} logo uploaded successfully`);
                    setUploadingHeaderLogo(prev => ({ ...prev, [logoType]: false }));
                    setUploadProgress(prev => ({ ...prev, [logoType]: 0 }));
                }
            );
        } catch (error) {
            console.error(`Error uploading ${logoType} logo:`, error);
            toast.error(`Failed to upload ${logoType} logo`);
            setUploadingHeaderLogo(prev => ({ ...prev, [logoType]: false }));
        }
    };

    // Footer background image upload handler
    const handleFooterBgUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadingFooterBg(true);
            const timestamp = Date.now();
            const fileName = `footer_bg_${timestamp}_${file.name}`;
            const storageRef = ref(storage, `cms-brand/footer/${fileName}`);

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            setFooterBackgroundImage(url);
            toast.success('Footer background image uploaded successfully');
        } catch (error) {
            console.error('Error uploading footer background:', error);
            toast.error('Failed to upload footer background image');
        } finally {
            setUploadingFooterBg(false);
        }
    };

    // Header navigation helpers
    const addNavLink = () => {
        const newLink = {
            id: `nav-${Date.now()}`,
            label: '',
            href: '/',
        };
        setMainNavLinks((prev) => [...prev, newLink]);
    };

    const updateNavLink = (id, field, value) => {
        setMainNavLinks((prev) => prev.map((link) => (link.id === id ? { ...link, [field]: value } : link)));
    };

    const removeNavLink = (id) => {
        setMainNavLinks((prev) => prev.filter((link) => link.id !== id));
    };

    // Footer links helpers
    const addFooterLink = () => {
        const newLink = {
            id: `footer-${Date.now()}`,
            section: 'General',
            label: '',
            href: '/',
        };
        setFooterLinks((prev) => [...prev, newLink]);
    };

    const updateFooterLink = (id, field, value) => {
        setFooterLinks((prev) => prev.map((link) => (link.id === id ? { ...link, [field]: value } : link)));
    };

    const removeFooterLink = (id) => {
        setFooterLinks((prev) => prev.filter((link) => link.id !== id));
    };

    // Header Actions Helpers
    const addHeaderAction = () => {
        const newAction = {
            id: `action-${Date.now()}`,
            type: 'link',
            label: 'New Action',
            href: '/',
            variant: 'ghost',
            showLabel: true
        };
        setHeaderActions([...headerActions, newAction]);
    };

    const updateHeaderAction = (id, field, value) => {
        setHeaderActions(headerActions.map(action =>
            action.id === id ? { ...action, [field]: value } : action
        ));
    };

    const removeHeaderAction = (id) => {
        setHeaderActions(headerActions.filter(action => action.id !== id));
    };

    const moveHeaderAction = (index, direction) => {
        if (direction === 'up' && index > 0) {
            const newActions = [...headerActions];
            [newActions[index], newActions[index - 1]] = [newActions[index - 1], newActions[index]];
            setHeaderActions(newActions);
        } else if (direction === 'down' && index < headerActions.length - 1) {
            const newActions = [...headerActions];
            [newActions[index], newActions[index + 1]] = [newActions[index + 1], newActions[index]];
            setHeaderActions(newActions);
        }
    };

    const saveBrandSettings = async () => {
        try {
            setSaving(true);

            const brandSettings = {
                brandName,
                brandTagline,
                logoUrl,
                faviconUrl,
                primaryColor,
                secondaryColor,
                fontFamily,
                contact: {
                    email: contactEmail,
                    phones: contactPhones,
                    address: {
                        street: contactAddressStreet,
                        area: contactAddressArea,
                        city: contactAddressCity,
                        province: contactAddressProvince,
                        country: contactAddressCountry,
                        postalCode: contactAddressPostalCode,
                        formatted: `${contactAddressStreet}, ${contactAddressArea}, ${contactAddressCity}, ${contactAddressProvince}, ${contactAddressCountry} ${contactAddressPostalCode}`
                    }
                },
                topbar: {
                    enabled: topbarEnabled,
                    text: topbarText,
                    linkLabel: topbarLinkLabel,
                    linkHref: topbarLinkHref,
                    bgColor: topbarBgColor,
                    textColor: topbarTextColor,
                    dismissable: topbarDismissable,
                    height: topbarHeight,
                    font: topbarFont,
                    fontSize: topbarFontSize,
                    fontWeight: topbarFontWeight,
                },
                mainNavLinks,
                footerLinks,
                typography: {
                    primaryFont,
                    secondaryFont,
                    headingWeight,
                    bodyWeight,
                    baseFontSize,
                    lineHeight,
                    letterSpacing,
                    linkUnderline,
                },
                header: {
                    // Basic
                    bgColor: headerBgColor,
                    textColor: headerTextColor,
                    height: headerHeight,
                    sticky: headerSticky,
                    mobileBreakpoint: headerMobileBreakpoint,
                    // Multiple Logos
                    logoDefault: headerLogoDefault,
                    logoSticky: headerLogoSticky,
                    logoTransparent: headerLogoTransparent,
                    logoMobile: headerLogoMobile,
                    logoLight: headerLogoLight,
                    logoWidth: headerLogoWidth,
                    // Transparency & Scroll
                    isTransparent: headerIsTransparent,
                    transparentBgColor: headerTransparentBgColor,
                    bgColorScrolled: headerBgColorScrolled,
                    textColorScrolled: headerTextColorScrolled,
                    heightScrolled: headerHeightScrolled,
                    scrollThreshold: headerScrollThreshold,
                    showShadowOnScroll: headerShowShadowOnScroll,
                    // Link Styling
                    linkColor: headerLinkColor,
                    linkHoverColor: headerLinkHoverColor,
                    linkActiveColor: headerLinkActiveColor,
                    // Border
                    borderEnabled: headerBorderEnabled,
                    borderColor: headerBorderColor,
                    borderWidth: headerBorderWidth,
                    // Typography
                    font: headerFont,
                    navFontSize: headerNavFontSize,
                    navFontWeight: headerNavFontWeight,
                    navLetterSpacing: headerNavLetterSpacing,
                    navTextTransform: headerNavTextTransform,
                    // Button Typography
                    buttonFontSize: headerButtonFontSize,
                    buttonFontWeight: headerButtonFontWeight,
                    buttonTextTransform: headerButtonTextTransform,
                    // Actions
                    actions: headerActions,
                    // Mobile Menu Settings
                    mobileMenu: {
                        position: mobileMenuPosition,
                        width: mobileMenuWidth,
                        bgColor: mobileMenuBgColor,
                        textColor: mobileMenuTextColor,
                        overlayColor: mobileMenuOverlayColor,
                        overlayBlur: mobileMenuOverlayBlur,
                        showLogo: mobileMenuShowLogo,
                        showCloseButton: mobileMenuShowCloseButton,
                        closeButtonPosition: mobileMenuCloseButtonPosition,
                        animation: mobileMenuAnimation,
                        animationDuration: mobileMenuAnimationDuration,
                        linkStyle: mobileMenuLinkStyle,
                        linkSpacing: mobileMenuLinkSpacing,
                        linkFontSize: mobileMenuLinkFontSize,
                        linkFontWeight: mobileMenuLinkFontWeight,
                        showDividers: mobileMenuShowDividers,
                        dividerColor: mobileMenuDividerColor,
                        buttonLayout: mobileMenuButtonLayout,
                        buttonSpacing: mobileMenuButtonSpacing,
                        headerHeight: mobileMenuHeaderHeight,
                        footerText: mobileMenuFooterText,
                        hamburgerStyle: mobileMenuHamburgerStyle,
                        hamburgerSize: mobileMenuHamburgerSize,
                        hamburgerLineWidth: mobileMenuHamburgerLineWidth,
                    }
                },
                footer: {
                    // Basic
                    bgColor: footerBgColor,
                    textColor: footerTextColor,
                    columns: footerColumns,
                    socialLinks: footerSocialLinks,
                    // Advanced
                    headingColor: footerHeadingColor,
                    linkColor: footerLinkColor,
                    linkHoverColor: footerLinkHoverColor,
                    layout: footerLayout,
                    showLogo: footerShowLogo,
                    showTagline: footerShowTagline,
                    showSocial: footerShowSocial,
                    showTrustBadges: footerShowTrustBadges,
                    copyrightText: footerCopyrightText,
                    backgroundImage: footerBackgroundImage,
                    backgroundOverlay: footerBackgroundOverlay,
                    // Powered By
                    showPoweredBy: footerShowPoweredBy,
                    poweredByText: footerPoweredByText,
                    poweredByLink: footerPoweredByLink,
                    poweredByLogoDarkUrl: footerPoweredByLogoDarkUrl,
                    poweredByLogoDarkHoverUrl: footerPoweredByLogoDarkHoverUrl,
                    poweredByLogoLightUrl: footerPoweredByLogoLightUrl,
                    poweredByLogoLightHoverUrl: footerPoweredByLogoLightHoverUrl,
                    // Typography
                    font: footerFont,
                    headingFontSize: footerHeadingFontSize,
                    headingFontWeight: footerHeadingFontWeight,
                    linkFontSize: footerLinkFontSize,
                    linkFontWeight: footerLinkFontWeight,
                },
                buttonStyles: {
                    primaryBg: buttonPrimaryBg,
                    primaryText: buttonPrimaryText,
                    primaryHoverBg: buttonPrimaryHoverBg,
                    secondaryBg: buttonSecondaryBg,
                    secondaryText: buttonSecondaryText,
                    secondaryBorder: buttonSecondaryBorder,
                    borderRadius: buttonBorderRadius,
                    paddingX: buttonPaddingX,
                    paddingY: buttonPaddingY
                },
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cms_global_settings', 'brand-settings'), brandSettings);
            toast.success('Brand settings saved successfully!');
        } catch (error) {
            console.error('Error saving brand settings:', error);
            toast.error('Error saving brand settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setMessage('');

            const settings = {
                socialProof: {
                    enabled: socialProofEnabled,
                    initialDelay: parseInt(socialProofInitialDelay),
                    interval: parseInt(socialProofInterval),
                    duration: parseInt(socialProofDuration),
                    notifications: socialProofNotifications
                },
                exitIntent: {
                    enabled: exitIntentEnabled,
                    cooldown: parseInt(exitIntentCooldown),
                    title: exitIntentTitle,
                    subtitle: exitIntentSubtitle,
                    benefits: exitIntentBenefits
                },
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cms_global_settings', 'conversion-widgets'), settings);
            setMessage('Settings saved successfully!');

            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            setMessage('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    const addNotification = () => {
        const newId = Math.max(...socialProofNotifications.map(n => n.id), 0) + 1;
        setSocialProofNotifications([
            ...socialProofNotifications,
            { id: newId, message: '', location: 'South Africa', time: 'Just now' }
        ]);
    };

    const updateNotification = (id, field, value) => {
        setSocialProofNotifications(
            socialProofNotifications.map(n =>
                n.id === id ? { ...n, [field]: value } : n
            )
        );
    };

    const removeNotification = (id) => {
        setSocialProofNotifications(socialProofNotifications.filter(n => n.id !== id));
    };

    const addBenefit = () => {
        setExitIntentBenefits([...exitIntentBenefits, '']);
    };

    const updateBenefit = (index, value) => {
        const newBenefits = [...exitIntentBenefits];
        newBenefits[index] = value;
        setExitIntentBenefits(newBenefits);
    };

    const removeBenefit = (index) => {
        setExitIntentBenefits(exitIntentBenefits.filter((_, i) => i !== index));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    // Breadcrumb component
    const Breadcrumbs = ({ items }) => (
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
            <Link to="/cms" className="hover:text-gray-900 flex items-center">
                <Home size={14} className="mr-1" />
                CMS
            </Link>
            {items.map((item, index) => (
                <React.Fragment key={index}>
                    <ChevronRight size={14} />
                    {item.href ? (
                        <Link to={item.href} className="hover:text-gray-900">{item.label}</Link>
                    ) : (
                        <span className="text-gray-900 font-medium">{item.label}</span>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Breadcrumbs */}
                <Breadcrumbs items={[{ label: 'Settings' }]} />

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold">Settings</h1>
                        <p className="text-muted-foreground mt-1">Manage your CMS configuration</p>
                    </div>
                    <Link to="/cms">
                        <Button variant="outline">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Dashboard
                        </Button>
                    </Link>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="flex-wrap h-auto gap-1">
                        <TabsTrigger value="brand">
                            <Palette className="h-4 w-4 mr-2" />
                            Brand & Design
                        </TabsTrigger>
                        <TabsTrigger value="typography">
                            <Type className="h-4 w-4 mr-2" />
                            Typography
                        </TabsTrigger>
                        <TabsTrigger value="layout">
                            <Layout className="h-4 w-4 mr-2" />
                            Header & Footer
                        </TabsTrigger>
                        <TabsTrigger value="mobile">
                            <Smartphone className="h-4 w-4 mr-2" />
                            Mobile Menu
                        </TabsTrigger>
                        <TabsTrigger value="buttons">
                            <MousePointer className="h-4 w-4 mr-2" />
                            Button Styles
                        </TabsTrigger>
                        <TabsTrigger value="conversion">
                            <Bell className="h-4 w-4 mr-2" />
                            Conversion Widgets
                        </TabsTrigger>
                        <TabsTrigger value="contact">
                            <Mail className="h-4 w-4 mr-2" />
                            Contact Info
                        </TabsTrigger>
                    </TabsList>

                    {/* Brand Settings Tab */}
                    <TabsContent value="brand" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Brand Identity</CardTitle>
                                <CardDescription>Configure your brand name, logos, and visual identity</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Brand Name & Tagline */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="brandName">Brand Name</Label>
                                        <Input
                                            id="brandName"
                                            value={brandName}
                                            onChange={(e) => setBrandName(e.target.value)}
                                            placeholder="ESGnet"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="brandTagline">Tagline</Label>
                                        <Input
                                            id="brandTagline"
                                            value={brandTagline}
                                            onChange={(e) => setBrandTagline(e.target.value)}
                                            placeholder="Simplifying ESG Compliance"
                                        />
                                    </div>
                                </div>

                                {/* Logo Upload */}
                                <div className="space-y-4">
                                    <div>
                                        <Label>Primary Logo</Label>
                                        <p className="text-sm text-muted-foreground mb-2">
                                            Recommended: PNG or SVG, max 2MB
                                        </p>
                                        <div className="flex items-center gap-4">
                                            {logoUrl && (
                                                <div className="w-32 h-32 border rounded-lg flex items-center justify-center bg-muted">
                                                    <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain p-2" />
                                                </div>
                                            )}
                                            <div>
                                                <input
                                                    ref={logoInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleLogoUpload(e, 'logo')}
                                                    className="hidden"
                                                />
                                                <Button
                                                    variant="outline"
                                                    onClick={() => logoInputRef.current?.click()}
                                                    disabled={uploadingLogo}
                                                >
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <Label>Favicon</Label>
                                        <p className="text-sm text-muted-foreground mb-2">
                                            Recommended: 32x32px or 64x64px PNG/ICO
                                        </p>
                                        <div className="flex items-center gap-4">
                                            {faviconUrl && (
                                                <div className="w-16 h-16 border rounded-lg flex items-center justify-center bg-muted">
                                                    <img src={faviconUrl} alt="Favicon" className="max-w-full max-h-full object-contain p-1" />
                                                </div>
                                            )}
                                            <div>
                                                <input
                                                    ref={faviconInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleLogoUpload(e, 'favicon')}
                                                    className="hidden"
                                                />
                                                <Button
                                                    variant="outline"
                                                    onClick={() => faviconInputRef.current?.click()}
                                                    disabled={uploadingFavicon}
                                                >
                                                    <Upload className="h-4 w-4 mr-2" />
                                                    {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Color Palette */}
                                <div>
                                    <Label className="mb-3 block">Color Palette</Label>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="primaryColor" className="text-sm">Primary Color</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    id="primaryColor"
                                                    type="color"
                                                    value={primaryColor}
                                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                                    className="w-20 h-10 p-1"
                                                />
                                                <Input
                                                    value={primaryColor}
                                                    onChange={(e) => setPrimaryColor(e.target.value)}
                                                    placeholder="#2e7a3b"
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="secondaryColor" className="text-sm">Secondary Color</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    id="secondaryColor"
                                                    type="color"
                                                    value={secondaryColor}
                                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                                    className="w-20 h-10 p-1"
                                                />
                                                <Input
                                                    value={secondaryColor}
                                                    onChange={(e) => setSecondaryColor(e.target.value)}
                                                    placeholder="#399c4a"
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Typography */}
                                <div className="space-y-2">
                                    <Label htmlFor="fontFamily">
                                        <Type className="h-4 w-4 inline mr-2" />
                                        Font Family
                                    </Label>
                                    <Input
                                        id="fontFamily"
                                        value={fontFamily}
                                        onChange={(e) => setFontFamily(e.target.value)}
                                        placeholder="Inter"
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        Enter a Google Font name or system font stack
                                    </p>
                                </div>

                                {/* Topbar Settings */}
                                <div className="pt-6 mt-2 border-t space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label>Topbar Announcement</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Optional strip above the header for promotions or announcements.
                                            </p>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={topbarEnabled}
                                                onChange={(e) => setTopbarEnabled(e.target.checked)}
                                                className="w-5 h-5 text-primary rounded focus:ring-primary"
                                            />
                                            <span className="text-sm font-medium">Enabled</span>
                                        </label>
                                    </div>

                                    {/* Topbar Content */}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="topbarText">Topbar Text</Label>
                                            <Input
                                                id="topbarText"
                                                value={topbarText}
                                                onChange={(e) => setTopbarText(e.target.value)}
                                                placeholder="Short announcement message..."
                                            />
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-2">
                                            <div className="space-y-2">
                                                <Label htmlFor="topbarLinkLabel">Link Label</Label>
                                                <Input
                                                    id="topbarLinkLabel"
                                                    value={topbarLinkLabel}
                                                    onChange={(e) => setTopbarLinkLabel(e.target.value)}
                                                    placeholder="Book Demo"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="topbarLinkHref">Link URL</Label>
                                                <Input
                                                    id="topbarLinkHref"
                                                    value={topbarLinkHref}
                                                    onChange={(e) => setTopbarLinkHref(e.target.value)}
                                                    placeholder="/demo or https://..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Topbar Styling */}
                                    <div className="grid md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Background Color</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={topbarBgColor}
                                                    onChange={(e) => setTopbarBgColor(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input
                                                    value={topbarBgColor}
                                                    onChange={(e) => setTopbarBgColor(e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Text Color</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={topbarTextColor}
                                                    onChange={(e) => setTopbarTextColor(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input
                                                    value={topbarTextColor}
                                                    onChange={(e) => setTopbarTextColor(e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Height (px)</Label>
                                            <Input
                                                type="number"
                                                min="30"
                                                max="60"
                                                value={topbarHeight}
                                                onChange={(e) => setTopbarHeight(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Dismissable</Label>
                                            <label className="flex items-center gap-2 cursor-pointer h-10">
                                                <input
                                                    type="checkbox"
                                                    checked={topbarDismissable}
                                                    onChange={(e) => setTopbarDismissable(e.target.checked)}
                                                    className="w-5 h-5 text-primary rounded focus:ring-primary"
                                                />
                                                <span className="text-sm">Allow users to dismiss</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Topbar Typography */}
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Font Family</Label>
                                            <select
                                                value={topbarFont || ''}
                                                onChange={(e) => setTopbarFont(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="">Inherit from body</option>
                                                {GOOGLE_FONTS.map(font => (
                                                    <option key={font.name} value={font.name}>{font.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Size (px)</Label>
                                            <Input
                                                type="number"
                                                min="10"
                                                max="18"
                                                value={topbarFontSize}
                                                onChange={(e) => setTopbarFontSize(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Weight</Label>
                                            <select
                                                value={topbarFontWeight}
                                                onChange={(e) => setTopbarFontWeight(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="400">Regular (400)</option>
                                                <option value="500">Medium (500)</option>
                                                <option value="600">Semi-Bold (600)</option>
                                                <option value="700">Bold (700)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Topbar Preview */}
                                    {topbarEnabled && topbarText && (
                                        <div className="mt-4">
                                            <Label className="mb-2 block">Preview</Label>
                                            <div
                                                className="rounded-lg overflow-hidden text-sm flex items-center justify-center gap-2"
                                                style={{
                                                    backgroundColor: topbarBgColor,
                                                    color: topbarTextColor,
                                                    height: `${topbarHeight}px`
                                                }}
                                            >
                                                <span>{topbarText}</span>
                                                {topbarLinkLabel && (
                                                    <span className="underline font-medium">{topbarLinkLabel}</span>
                                                )}
                                                {topbarDismissable && (
                                                    <span className="ml-2 opacity-70">✕</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Header Navigation */}
                                <div className="pt-6 mt-2 border-t space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label>Header Navigation</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Links shown in the main header next to the logo.
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={addNavLink}
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Link
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setMainNavLinks(defaultBrandSettings.mainNavLinks)}
                                            >
                                                Reset to Defaults
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        {mainNavLinks.map((link) => (
                                            <div
                                                key={link.id}
                                                className="flex flex-col md:flex-row gap-2 items-start md:items-center"
                                            >
                                                <Input
                                                    value={link.label}
                                                    onChange={(e) => updateNavLink(link.id, 'label', e.target.value)}
                                                    placeholder="Label (e.g. Platform)"
                                                    className="md:flex-1"
                                                />
                                                <Input
                                                    value={link.href}
                                                    onChange={(e) => updateNavLink(link.id, 'href', e.target.value)}
                                                    placeholder="/pricing or /#features"
                                                    className="md:flex-1"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removeNavLink(link.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                        {mainNavLinks.length === 0 && (
                                            <p className="text-xs text-muted-foreground">
                                                No links configured. The default navigation will be used.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Header Actions */}
                                <div className="pt-6 mt-2 border-t space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label>Header Actions</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Buttons and icons (e.g. Login, Book Demo) shown on the right side.
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={addHeaderAction}
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Add Action
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setHeaderActions(defaultBrandSettings.header.actions)}
                                            >
                                                Reset
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        {headerActions.map((action, index) => (
                                            <div key={action.id} className="p-4 border rounded-lg space-y-4 bg-muted/10">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                                                        <div className="col-span-2 md:col-span-1">
                                                            <Label className="text-xs mb-1 block">Label</Label>
                                                            <Input
                                                                value={action.label}
                                                                onChange={(e) => updateHeaderAction(action.id, 'label', e.target.value)}
                                                                placeholder="Label"
                                                                className="h-8 text-sm"
                                                            />
                                                        </div>
                                                        <div className="col-span-2 md:col-span-1">
                                                            <Label className="text-xs mb-1 block">URL</Label>
                                                            <Input
                                                                value={action.href}
                                                                onChange={(e) => updateHeaderAction(action.id, 'href', e.target.value)}
                                                                placeholder="https://..."
                                                                className="h-8 text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs mb-1 block">Type</Label>
                                                            <select
                                                                value={action.type}
                                                                onChange={(e) => updateHeaderAction(action.id, 'type', e.target.value)}
                                                                className="w-full h-8 text-sm px-2 border rounded-md bg-background text-[#222224]"
                                                            >
                                                                <option value="link">Link/Button</option>
                                                                <option value="icon">Icon Only</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs mb-1 block">Variant</Label>
                                                            <select
                                                                value={action.variant}
                                                                onChange={(e) => updateHeaderAction(action.id, 'variant', e.target.value)}
                                                                className="w-full h-8 text-sm px-2 border rounded-md bg-background text-[#222224]"
                                                            >
                                                                <option value="primary">Primary</option>
                                                                <option value="secondary">Secondary</option>
                                                                <option value="outline">Outline</option>
                                                                <option value="ghost">Ghost</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => moveHeaderAction(index, 'up')}
                                                            disabled={index === 0}
                                                        >
                                                            <ChevronDown className="h-4 w-4 rotate-180" />
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => moveHeaderAction(index, 'down')}
                                                            disabled={index === headerActions.length - 1}
                                                        >
                                                            <ChevronDown className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                        onClick={() => removeHeaderAction(action.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                
                                                <div className="flex flex-wrap gap-4 items-end border-t pt-3 border-dashed">
                                                    <div className="w-32">
                                                        <Label className="text-xs mb-1 block">Icon Name</Label>
                                                        <Input
                                                            value={action.icon || ''}
                                                            onChange={(e) => updateHeaderAction(action.id, 'icon', e.target.value)}
                                                            placeholder="e.g. search, user"
                                                            className="h-8 text-sm"
                                                        />
                                                    </div>
                                                    <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={action.showLabel !== false}
                                                            onChange={(e) => updateHeaderAction(action.id, 'showLabel', e.target.checked)}
                                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                        />
                                                        <span className="text-sm">Show Label</span>
                                                    </label>
                                                    <div className="flex-1">
                                                        <Label className="text-xs mb-1 block">Visibility Class</Label>
                                                        <Input
                                                            value={action.className || ''}
                                                            onChange={(e) => updateHeaderAction(action.id, 'className', e.target.value)}
                                                            placeholder="e.g. hidden sm:block"
                                                            className="h-8 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {headerActions.length === 0 && (
                                            <p className="text-xs text-muted-foreground text-center py-4 bg-muted/20 rounded-lg border border-dashed">
                                                No actions configured. Add a button or icon.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Footer Links */}
                                <div className="pt-6 mt-2 border-t space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label>Footer Links</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Control sections and links in the global footer.
                                            </p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={addFooterLink}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Footer Link
                                        </Button>
                                    </div>
                                    <div className="space-y-3">
                                        {footerLinks.map((link) => (
                                            <div
                                                key={link.id}
                                                className="grid md:grid-cols-3 gap-2 items-center"
                                            >
                                                <Input
                                                    value={link.section || ''}
                                                    onChange={(e) => updateFooterLink(link.id, 'section', e.target.value)}
                                                    placeholder="Section (e.g. Platform, Company)"
                                                />
                                                <Input
                                                    value={link.label}
                                                    onChange={(e) => updateFooterLink(link.id, 'label', e.target.value)}
                                                    placeholder="Link label"
                                                />
                                                <div className="flex gap-2 items-center">
                                                    <Input
                                                        value={link.href}
                                                        onChange={(e) => updateFooterLink(link.id, 'href', e.target.value)}
                                                        placeholder="/pricing or https://..."
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeFooterLink(link.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {footerLinks.length === 0 && (
                                            <p className="text-xs text-muted-foreground">
                                                No footer links configured. The default footer will be used.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Brand Settings'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Typography Tab */}
                    <TabsContent value="typography" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Typography Settings</CardTitle>
                                <CardDescription>Configure fonts, sizes, and text styling for your site</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Font Selection */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    <FontSelector
                                        value={primaryFont}
                                        onChange={setPrimaryFont}
                                        label="Primary Font (Headings)"
                                        description="Used for headings and titles"
                                    />
                                    <FontSelector
                                        value={secondaryFont}
                                        onChange={setSecondaryFont}
                                        label="Secondary Font (Body)"
                                        description="Used for body text and paragraphs"
                                    />
                                </div>

                                {/* Font Weights */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label>Heading Weight</Label>
                                        <select
                                            value={headingWeight}
                                            onChange={(e) => setHeadingWeight(e.target.value)}
                                            className="w-full px-3 py-2 border rounded-md bg-background text-[#222224]"
                                        >
                                            <option value="400">Regular (400)</option>
                                            <option value="500">Medium (500)</option>
                                            <option value="600">Semi-Bold (600)</option>
                                            <option value="700">Bold (700)</option>
                                            <option value="800">Extra Bold (800)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Body Weight</Label>
                                        <select
                                            value={bodyWeight}
                                            onChange={(e) => setBodyWeight(e.target.value)}
                                            className="w-full px-3 py-2 border rounded-md bg-background text-[#222224]"
                                        >
                                            <option value="300">Light (300)</option>
                                            <option value="400">Regular (400)</option>
                                            <option value="500">Medium (500)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Size & Spacing */}
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <Label>Base Font Size (px)</Label>
                                        <Input
                                            type="number"
                                            value={baseFontSize}
                                            onChange={(e) => setBaseFontSize(e.target.value)}
                                            min="12"
                                            max="24"
                                        />
                                        <p className="text-xs text-muted-foreground">Recommended: 16px</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Line Height</Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            value={lineHeight}
                                            onChange={(e) => setLineHeight(e.target.value)}
                                            min="1"
                                            max="2.5"
                                        />
                                        <p className="text-xs text-muted-foreground">Recommended: 1.5-1.7</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Letter Spacing (em)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={letterSpacing}
                                            onChange={(e) => setLetterSpacing(e.target.value)}
                                            min="-0.1"
                                            max="0.2"
                                        />
                                        <p className="text-xs text-muted-foreground">0 = normal</p>
                                    </div>
                                </div>

                                {/* Link Styling */}
                                <div className="space-y-4">
                                    <h4 className="font-medium">Link Styling</h4>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            id="linkUnderline"
                                            checked={linkUnderline}
                                            onChange={(e) => setLinkUnderline(e.target.checked)}
                                            className="w-5 h-5 text-primary rounded focus:ring-primary"
                                        />
                                        <Label htmlFor="linkUnderline" className="cursor-pointer">
                                            Underline links by default
                                        </Label>
                                    </div>
                                    <p className="text-xs text-muted-foreground">When enabled, all text links will be underlined. Links will always underline on hover.</p>
                                </div>

                                {/* Preview */}
                                <div className="p-6 border rounded-lg bg-muted/30">
                                    <h4 className="text-sm font-medium mb-4">Preview</h4>
                                    <div style={{ fontFamily: primaryFont, fontWeight: headingWeight }}>
                                        <h1 className="text-3xl mb-2">Heading Example</h1>
                                    </div>
                                    <div style={{ fontFamily: secondaryFont, fontWeight: bodyWeight, fontSize: `${baseFontSize}px`, lineHeight: lineHeight, letterSpacing: `${letterSpacing}em` }}>
                                        <p className="text-muted-foreground">This is body text that demonstrates how your typography settings will appear on the site. Good typography improves readability and user experience.</p>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Typography Settings'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Header & Footer Tab */}
                    <TabsContent value="layout" className="space-y-6">
                        {/* Header Settings - Full Width */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Layout className="h-5 w-5" />
                                    Advanced Header Settings
                                </CardTitle>
                                <CardDescription>Blocksy/Elementor-level header controls with multiple logos, transparency, and scroll behaviors</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Basic Colors & Dimensions */}
                                <div>
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Basic Settings</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Background Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerBgColor} onChange={(e) => setHeaderBgColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Text Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerTextColor} onChange={(e) => setHeaderTextColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Height (px)</Label>
                                            <Input type="number" value={headerHeight} onChange={(e) => setHeaderHeight(e.target.value)} min="48" max="120" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Mobile Breakpoint (px)</Label>
                                            <Input type="number" value={headerMobileBreakpoint} onChange={(e) => setHeaderMobileBreakpoint(e.target.value)} min="480" max="1024" />
                                        </div>
                                    </div>
                                </div>

                                {/* Multiple Logo States */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Multiple Logo States</h4>
                                    <p className="text-sm text-muted-foreground mb-4">Configure different logos for different navbar states (leave empty to use main logo)</p>

                                    {/* Logo Width Control */}
                                    <div className="mb-6 p-4 bg-muted/30 rounded-lg">
                                        <Label className="mb-2 block">Logo Width (px)</Label>
                                        <div className="flex items-center gap-4">
                                            <Input
                                                type="number"
                                                value={headerLogoWidth}
                                                onChange={(e) => setHeaderLogoWidth(e.target.value)}
                                                min="80"
                                                max="300"
                                                className="w-24"
                                            />
                                            <input
                                                type="range"
                                                value={headerLogoWidth}
                                                onChange={(e) => setHeaderLogoWidth(e.target.value)}
                                                min="80"
                                                max="300"
                                                className="flex-1"
                                            />
                                            <span className="text-sm text-muted-foreground w-16">{headerLogoWidth}px</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">Set the display width for logos (80-300px)</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Default Logo */}
                                        <div className="space-y-3 p-4 border rounded-lg">
                                            <Label>Default Logo</Label>
                                            <p className="text-xs text-muted-foreground">Normal header state</p>
                                            <div className="flex items-center gap-4">
                                                {headerLogoDefault && (
                                                    <div className="w-24 h-16 border rounded-lg flex items-center justify-center bg-muted p-2">
                                                        <img src={headerLogoDefault} alt="Default Logo" className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input ref={headerLogoDefaultRef} type="file" accept="image/*" onChange={(e) => handleHeaderLogoUpload(e, 'default')} className="hidden" />
                                                    <Button variant="outline" size="sm" onClick={() => headerLogoDefaultRef.current?.click()} disabled={uploadingHeaderLogo.default}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {uploadingHeaderLogo.default ? `${uploadProgress.default}%` : 'Upload'}
                                                    </Button>
                                                    {headerLogoDefault && (
                                                        <Button variant="ghost" size="sm" onClick={() => setHeaderLogoDefault('')} className="ml-2">
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadingHeaderLogo.default && (
                                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.default}%` }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Sticky Logo */}
                                        <div className="space-y-3 p-4 border rounded-lg">
                                            <Label>Sticky Logo</Label>
                                            <p className="text-xs text-muted-foreground">When scrolled/sticky</p>
                                            <div className="flex items-center gap-4">
                                                {headerLogoSticky && (
                                                    <div className="w-24 h-16 border rounded-lg flex items-center justify-center bg-muted p-2">
                                                        <img src={headerLogoSticky} alt="Sticky Logo" className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input ref={headerLogoStickyRef} type="file" accept="image/*" onChange={(e) => handleHeaderLogoUpload(e, 'sticky')} className="hidden" />
                                                    <Button variant="outline" size="sm" onClick={() => headerLogoStickyRef.current?.click()} disabled={uploadingHeaderLogo.sticky}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {uploadingHeaderLogo.sticky ? `${uploadProgress.sticky}%` : 'Upload'}
                                                    </Button>
                                                    {headerLogoSticky && (
                                                        <Button variant="ghost" size="sm" onClick={() => setHeaderLogoSticky('')} className="ml-2">
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadingHeaderLogo.sticky && (
                                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.sticky}%` }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Transparent Logo */}
                                        <div className="space-y-3 p-4 border rounded-lg">
                                            <Label>Transparent Logo</Label>
                                            <p className="text-xs text-muted-foreground">For hero sections with transparent header</p>
                                            <div className="flex items-center gap-4">
                                                {headerLogoTransparent && (
                                                    <div className="w-24 h-16 border rounded-lg flex items-center justify-center bg-gray-800 p-2">
                                                        <img src={headerLogoTransparent} alt="Transparent Logo" className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input ref={headerLogoTransparentRef} type="file" accept="image/*" onChange={(e) => handleHeaderLogoUpload(e, 'transparent')} className="hidden" />
                                                    <Button variant="outline" size="sm" onClick={() => headerLogoTransparentRef.current?.click()} disabled={uploadingHeaderLogo.transparent}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {uploadingHeaderLogo.transparent ? `${uploadProgress.transparent}%` : 'Upload'}
                                                    </Button>
                                                    {headerLogoTransparent && (
                                                        <Button variant="ghost" size="sm" onClick={() => setHeaderLogoTransparent('')} className="ml-2">
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadingHeaderLogo.transparent && (
                                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.transparent}%` }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Mobile Logo */}
                                        <div className="space-y-3 p-4 border rounded-lg">
                                            <Label>Mobile Logo</Label>
                                            <p className="text-xs text-muted-foreground">Optional smaller logo for mobile</p>
                                            <div className="flex items-center gap-4">
                                                {headerLogoMobile && (
                                                    <div className="w-24 h-16 border rounded-lg flex items-center justify-center bg-muted p-2">
                                                        <img src={headerLogoMobile} alt="Mobile Logo" className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input ref={headerLogoMobileRef} type="file" accept="image/*" onChange={(e) => handleHeaderLogoUpload(e, 'mobile')} className="hidden" />
                                                    <Button variant="outline" size="sm" onClick={() => headerLogoMobileRef.current?.click()} disabled={uploadingHeaderLogo.mobile}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {uploadingHeaderLogo.mobile ? `${uploadProgress.mobile}%` : 'Upload'}
                                                    </Button>
                                                    {headerLogoMobile && (
                                                        <Button variant="ghost" size="sm" onClick={() => setHeaderLogoMobile('')} className="ml-2">
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadingHeaderLogo.mobile && (
                                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.mobile}%` }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Light Logo (for dark backgrounds) */}
                                        <div className="space-y-2">
                                            <Label>Light/White Logo</Label>
                                            <p className="text-xs text-muted-foreground">For dark backgrounds (footer, dark sections)</p>
                                            <div className="flex items-center gap-4">
                                                {headerLogoLight && (
                                                    <div className="w-24 h-16 border rounded-lg flex items-center justify-center bg-gray-800 p-2">
                                                        <img src={headerLogoLight} alt="Light Logo" className="max-w-full max-h-full object-contain" />
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input ref={headerLogoLightRef} type="file" accept="image/*" onChange={(e) => handleHeaderLogoUpload(e, 'light')} className="hidden" />
                                                    <Button variant="outline" size="sm" onClick={() => headerLogoLightRef.current?.click()} disabled={uploadingHeaderLogo.light}>
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {uploadingHeaderLogo.light ? `${uploadProgress.light}%` : 'Upload'}
                                                    </Button>
                                                    {headerLogoLight && (
                                                        <Button variant="ghost" size="sm" onClick={() => setHeaderLogoLight('')} className="ml-2">
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {uploadingHeaderLogo.light && (
                                                <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress.light}%` }} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Transparency & Scroll Behavior */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Transparency & Scroll Behavior</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        <div className="flex items-center justify-between col-span-2 p-3 bg-muted/50 rounded-lg">
                                            <div>
                                                <Label>Transparent Header</Label>
                                                <p className="text-xs text-muted-foreground">Start transparent, add bg on scroll</p>
                                            </div>
                                            <input type="checkbox" checked={headerIsTransparent} onChange={(e) => setHeaderIsTransparent(e.target.checked)} className="w-5 h-5" />
                                        </div>
                                        <div className="flex items-center justify-between col-span-2 p-3 bg-muted/50 rounded-lg">
                                            <div>
                                                <Label>Sticky Header</Label>
                                                <p className="text-xs text-muted-foreground">Header stays visible when scrolling</p>
                                            </div>
                                            <input type="checkbox" checked={headerSticky} onChange={(e) => setHeaderSticky(e.target.checked)} className="w-5 h-5" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Scrolled BG Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerBgColorScrolled} onChange={(e) => setHeaderBgColorScrolled(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerBgColorScrolled} onChange={(e) => setHeaderBgColorScrolled(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Scrolled Text Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerTextColorScrolled} onChange={(e) => setHeaderTextColorScrolled(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerTextColorScrolled} onChange={(e) => setHeaderTextColorScrolled(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Scrolled Height (px)</Label>
                                            <Input type="number" value={headerHeightScrolled} onChange={(e) => setHeaderHeightScrolled(e.target.value)} min="40" max="100" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Scroll Threshold (px)</Label>
                                            <Input type="number" value={headerScrollThreshold} onChange={(e) => setHeaderScrollThreshold(e.target.value)} min="0" max="500" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 mt-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={headerShowShadowOnScroll} onChange={(e) => setHeaderShowShadowOnScroll(e.target.checked)} className="w-4 h-4" />
                                            <span className="text-sm">Show shadow on scroll</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Link Styling */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Navigation Link Styling</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Link Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerLinkColor} onChange={(e) => setHeaderLinkColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerLinkColor} onChange={(e) => setHeaderLinkColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Hover Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerLinkHoverColor} onChange={(e) => setHeaderLinkHoverColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerLinkHoverColor} onChange={(e) => setHeaderLinkHoverColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Active Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerLinkActiveColor} onChange={(e) => setHeaderLinkActiveColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerLinkActiveColor} onChange={(e) => setHeaderLinkActiveColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Border Settings */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Border</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={headerBorderEnabled} onChange={(e) => setHeaderBorderEnabled(e.target.checked)} className="w-4 h-4" />
                                                <span className="text-sm">Enable Border</span>
                                            </label>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Border Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={headerBorderColor} onChange={(e) => setHeaderBorderColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={headerBorderColor} onChange={(e) => setHeaderBorderColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Border Width (px)</Label>
                                            <Input type="number" value={headerBorderWidth} onChange={(e) => setHeaderBorderWidth(e.target.value)} min="0" max="5" />
                                        </div>
                                    </div>
                                </div>

                                {/* Header Typography */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Navigation Typography</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                        <div className="space-y-2">
                                            <Label>Font Family</Label>
                                            <select
                                                value={headerFont || ''}
                                                onChange={(e) => setHeaderFont(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="">Inherit from body</option>
                                                {GOOGLE_FONTS.map(font => (
                                                    <option key={font.name} value={font.name}>{font.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Size (px)</Label>
                                            <Input type="number" value={headerNavFontSize} onChange={(e) => setHeaderNavFontSize(e.target.value)} min="10" max="20" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Weight</Label>
                                            <select
                                                value={headerNavFontWeight}
                                                onChange={(e) => setHeaderNavFontWeight(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="400">Regular (400)</option>
                                                <option value="500">Medium (500)</option>
                                                <option value="600">Semi-Bold (600)</option>
                                                <option value="700">Bold (700)</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Letter Spacing (em)</Label>
                                            <Input type="number" step="0.01" value={headerNavLetterSpacing} onChange={(e) => setHeaderNavLetterSpacing(e.target.value)} min="-0.05" max="0.2" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Text Transform</Label>
                                            <select
                                                value={headerNavTextTransform}
                                                onChange={(e) => setHeaderNavTextTransform(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="none">None</option>
                                                <option value="uppercase">UPPERCASE</option>
                                                <option value="lowercase">lowercase</option>
                                                <option value="capitalize">Capitalize</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Header Settings'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Advanced Footer Settings - Full Width */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Layout className="h-5 w-5" />
                                    Advanced Footer Settings
                                </CardTitle>
                                <CardDescription>Multi-column footer with advanced styling, social links, and trust badges</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Basic Colors */}
                                <div>
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Colors</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Background Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={footerBgColor} onChange={(e) => setFooterBgColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={footerBgColor} onChange={(e) => setFooterBgColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Text Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={footerTextColor} onChange={(e) => setFooterTextColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={footerTextColor} onChange={(e) => setFooterTextColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Heading Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={footerHeadingColor} onChange={(e) => setFooterHeadingColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={footerHeadingColor} onChange={(e) => setFooterHeadingColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Link Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={footerLinkColor} onChange={(e) => setFooterLinkColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={footerLinkColor} onChange={(e) => setFooterLinkColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Layout & Display Options */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Layout & Display</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        <div className="space-y-2">
                                            <Label>Footer Columns</Label>
                                            <select value={footerColumns} onChange={(e) => setFooterColumns(e.target.value)} className="w-full px-3 py-2 border rounded-md bg-background text-[#222224]">
                                                <option value="2">2 Columns</option>
                                                <option value="3">3 Columns</option>
                                                <option value="4">4 Columns</option>
                                                <option value="5">5 Columns</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Layout Style</Label>
                                            <select value={footerLayout} onChange={(e) => setFooterLayout(e.target.value)} className="w-full px-3 py-2 border rounded-md bg-background text-[#222224]">
                                                <option value="standard">Standard</option>
                                                <option value="centered">Centered</option>
                                                <option value="minimal">Minimal</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Link Hover Color</Label>
                                            <div className="flex gap-2">
                                                <input type="color" value={footerLinkHoverColor} onChange={(e) => setFooterLinkHoverColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
                                                <Input value={footerLinkHoverColor} onChange={(e) => setFooterLinkHoverColor(e.target.value)} className="flex-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <label className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={footerShowLogo} onChange={(e) => setFooterShowLogo(e.target.checked)} className="w-4 h-4" />
                                            <span className="text-sm">Show Logo</span>
                                        </label>
                                        <label className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={footerShowTagline} onChange={(e) => setFooterShowTagline(e.target.checked)} className="w-4 h-4" />
                                            <span className="text-sm">Show Tagline</span>
                                        </label>
                                        <label className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={footerShowSocial} onChange={(e) => setFooterShowSocial(e.target.checked)} className="w-4 h-4" />
                                            <span className="text-sm">Show Social Icons</span>
                                        </label>
                                        <label className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg cursor-pointer">
                                            <input type="checkbox" checked={footerShowTrustBadges} onChange={(e) => setFooterShowTrustBadges(e.target.checked)} className="w-4 h-4" />
                                            <span className="text-sm">Show Trust Badges</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Social Links */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Social Links</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {footerSocialLinks.map((social, index) => (
                                            <div key={social.platform} className="flex gap-2 items-center">
                                                <span className="w-24 text-sm capitalize font-medium">{social.platform}</span>
                                                <Input
                                                    value={social.url}
                                                    onChange={(e) => {
                                                        const updated = [...footerSocialLinks];
                                                        updated[index].url = e.target.value;
                                                        setFooterSocialLinks(updated);
                                                    }}
                                                    placeholder={`https://${social.platform}.com/...`}
                                                    className="flex-1"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Copyright & Background */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Copyright & Background</h4>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Copyright Text</Label>
                                            <Input
                                                value={footerCopyrightText}
                                                onChange={(e) => setFooterCopyrightText(e.target.value)}
                                                placeholder="© {year} {brandName}. All rights reserved."
                                            />
                                            <p className="text-xs text-muted-foreground">Use {'{year}'} for current year, {'{brandName}'} for brand name</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Background Image</Label>
                                                <div className="flex gap-2">
                                                    <Input
                                                        value={footerBackgroundImage}
                                                        onChange={(e) => setFooterBackgroundImage(e.target.value)}
                                                        placeholder="https://..."
                                                        className="flex-1"
                                                    />
                                                    <input
                                                        ref={footerBgInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleFooterBgUpload}
                                                        className="hidden"
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => footerBgInputRef.current?.click()}
                                                        disabled={uploadingFooterBg}
                                                    >
                                                        <Upload className="h-4 w-4 mr-1" />
                                                        {uploadingFooterBg ? 'Uploading...' : 'Upload'}
                                                    </Button>
                                                    {footerBackgroundImage && (
                                                        <Button variant="ghost" size="sm" onClick={() => setFooterBackgroundImage('')}>
                                                            <Trash2 className="h-4 w-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </div>
                                                {footerBackgroundImage && (
                                                    <div className="w-full h-20 border rounded-md overflow-hidden mt-2">
                                                        <img src={footerBackgroundImage} alt="Footer background" className="w-full h-full object-cover" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Background Overlay</Label>
                                                <Input value={footerBackgroundOverlay} onChange={(e) => setFooterBackgroundOverlay(e.target.value)} placeholder="rgba(17,24,39,0.95)" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Powered By Branding */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Powered By Branding</h4>
                                    <p className="text-xs text-muted-foreground mb-4">Configure the "Powered By" section that appears in the footer. Upload logos for white-labeling or use text.</p>
                                    
                                    <div className="space-y-4">
                                        {/* Toggle & Text */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <Label>Show Powered By</Label>
                                                <p className="text-xs text-muted-foreground">Display the powered by section in the footer</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={footerShowPoweredBy}
                                                onChange={(e) => setFooterShowPoweredBy(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                        </div>
                                        
                                        {footerShowPoweredBy && (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Powered By Text</Label>
                                                        <Input
                                                            value={footerPoweredByText}
                                                            onChange={(e) => setFooterPoweredByText(e.target.value)}
                                                            placeholder="Powered by Spout Technologies"
                                                        />
                                                        <p className="text-xs text-muted-foreground">Text displayed when no logo is set, or as alt text</p>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Powered By Link</Label>
                                                        <Input
                                                            value={footerPoweredByLink}
                                                            onChange={(e) => setFooterPoweredByLink(e.target.value)}
                                                            placeholder="https://spout.co.za"
                                                            type="url"
                                                        />
                                                        <p className="text-xs text-muted-foreground">URL to open when clicking the powered by text/logo</p>
                                                    </div>
                                                </div>

                                                {/* Powered By Logos */}
                                                <div className="space-y-3">
                                                    <Label className="text-sm font-medium">Powered By Logos</Label>
                                                    <p className="text-xs text-muted-foreground">Upload logos for different themes and hover states. If no logos are uploaded, the text will be displayed instead.</p>
                                                    
                                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                                        {/* Dark Logo (for light backgrounds) */}
                                                        <div className="border rounded-lg p-4">
                                                            <Label className="text-sm font-medium">Dark Logo</Label>
                                                            <p className="text-xs text-muted-foreground mb-3">For light backgrounds</p>
                                                            <div
                                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary cursor-pointer transition-colors bg-white"
                                                                onClick={() => {
                                                                    const input = document.createElement('input');
                                                                    input.type = 'file';
                                                                    input.accept = 'image/*';
                                                                    input.onchange = async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            const storageRef = ref(storage, `cms/powered-by/dark-${Date.now()}.${file.name.split('.').pop()}`);
                                                                            await uploadBytes(storageRef, file);
                                                                            const url = await getDownloadURL(storageRef);
                                                                            setFooterPoweredByLogoDarkUrl(url);
                                                                        }
                                                                    };
                                                                    input.click();
                                                                }}
                                                            >
                                                                {footerPoweredByLogoDarkUrl ? (
                                                                    <div className="space-y-2">
                                                                        <img src={footerPoweredByLogoDarkUrl} alt="Dark Logo" className="h-8 mx-auto object-contain" />
                                                                        <p className="text-xs text-muted-foreground">Click to replace</p>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                                                                        <p className="text-xs text-muted-foreground">Click to upload</p>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {footerPoweredByLogoDarkUrl && (
                                                                <Button variant="ghost" size="sm" className="w-full mt-2 text-destructive" onClick={() => setFooterPoweredByLogoDarkUrl('')}>
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>

                                                        {/* Dark Logo Hover */}
                                                        <div className="border rounded-lg p-4">
                                                            <Label className="text-sm font-medium">Dark Logo (Hover)</Label>
                                                            <p className="text-xs text-muted-foreground mb-3">Hover state on light bg</p>
                                                            <div
                                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary cursor-pointer transition-colors bg-gray-100"
                                                                onClick={() => {
                                                                    const input = document.createElement('input');
                                                                    input.type = 'file';
                                                                    input.accept = 'image/*';
                                                                    input.onchange = async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            const storageRef = ref(storage, `cms/powered-by/dark-hover-${Date.now()}.${file.name.split('.').pop()}`);
                                                                            await uploadBytes(storageRef, file);
                                                                            const url = await getDownloadURL(storageRef);
                                                                            setFooterPoweredByLogoDarkHoverUrl(url);
                                                                        }
                                                                    };
                                                                    input.click();
                                                                }}
                                                            >
                                                                {footerPoweredByLogoDarkHoverUrl ? (
                                                                    <div className="space-y-2">
                                                                        <img src={footerPoweredByLogoDarkHoverUrl} alt="Dark Logo Hover" className="h-8 mx-auto object-contain" />
                                                                        <p className="text-xs text-muted-foreground">Click to replace</p>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                                                                        <p className="text-xs text-muted-foreground">Click to upload</p>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {footerPoweredByLogoDarkHoverUrl && (
                                                                <Button variant="ghost" size="sm" className="w-full mt-2 text-destructive" onClick={() => setFooterPoweredByLogoDarkHoverUrl('')}>
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>

                                                        {/* Light Logo (for dark backgrounds) */}
                                                        <div className="border rounded-lg p-4">
                                                            <Label className="text-sm font-medium">Light Logo</Label>
                                                            <p className="text-xs text-muted-foreground mb-3">For dark backgrounds</p>
                                                            <div
                                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary cursor-pointer transition-colors bg-slate-800"
                                                                onClick={() => {
                                                                    const input = document.createElement('input');
                                                                    input.type = 'file';
                                                                    input.accept = 'image/*';
                                                                    input.onchange = async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            const storageRef = ref(storage, `cms/powered-by/light-${Date.now()}.${file.name.split('.').pop()}`);
                                                                            await uploadBytes(storageRef, file);
                                                                            const url = await getDownloadURL(storageRef);
                                                                            setFooterPoweredByLogoLightUrl(url);
                                                                        }
                                                                    };
                                                                    input.click();
                                                                }}
                                                            >
                                                                {footerPoweredByLogoLightUrl ? (
                                                                    <div className="space-y-2">
                                                                        <img src={footerPoweredByLogoLightUrl} alt="Light Logo" className="h-8 mx-auto object-contain" />
                                                                        <p className="text-xs text-gray-300">Click to replace</p>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <Upload className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                                                                        <p className="text-xs text-gray-300">Click to upload</p>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {footerPoweredByLogoLightUrl && (
                                                                <Button variant="ghost" size="sm" className="w-full mt-2 text-destructive" onClick={() => setFooterPoweredByLogoLightUrl('')}>
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>

                                                        {/* Light Logo Hover */}
                                                        <div className="border rounded-lg p-4">
                                                            <Label className="text-sm font-medium">Light Logo (Hover)</Label>
                                                            <p className="text-xs text-muted-foreground mb-3">Hover state on dark bg</p>
                                                            <div
                                                                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary cursor-pointer transition-colors bg-slate-700"
                                                                onClick={() => {
                                                                    const input = document.createElement('input');
                                                                    input.type = 'file';
                                                                    input.accept = 'image/*';
                                                                    input.onchange = async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (file) {
                                                                            const storageRef = ref(storage, `cms/powered-by/light-hover-${Date.now()}.${file.name.split('.').pop()}`);
                                                                            await uploadBytes(storageRef, file);
                                                                            const url = await getDownloadURL(storageRef);
                                                                            setFooterPoweredByLogoLightHoverUrl(url);
                                                                        }
                                                                    };
                                                                    input.click();
                                                                }}
                                                            >
                                                                {footerPoweredByLogoLightHoverUrl ? (
                                                                    <div className="space-y-2">
                                                                        <img src={footerPoweredByLogoLightHoverUrl} alt="Light Logo Hover" className="h-8 mx-auto object-contain" />
                                                                        <p className="text-xs text-gray-300">Click to replace</p>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <Upload className="h-6 w-6 mx-auto mb-1 text-gray-300" />
                                                                        <p className="text-xs text-gray-300">Click to upload</p>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {footerPoweredByLogoLightHoverUrl && (
                                                                <Button variant="ghost" size="sm" className="w-full mt-2 text-destructive" onClick={() => setFooterPoweredByLogoLightHoverUrl('')}>
                                                                    Remove
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Preview */}
                                                {(footerPoweredByLogoLightUrl || footerPoweredByText) && (
                                                    <div className="border rounded-lg p-4 bg-gray-50">
                                                        <Label className="text-sm font-medium mb-3 block">Preview</Label>
                                                        <div className="flex items-center gap-6">
                                                            <div className="flex-1 p-4 bg-white rounded border">
                                                                <p className="text-xs text-muted-foreground mb-2">Light Background</p>
                                                                {footerPoweredByLink ? (
                                                                    <a href={footerPoweredByLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                                                                        {footerPoweredByLogoDarkUrl ? (
                                                                            <img src={footerPoweredByLogoDarkUrl} alt={footerPoweredByText || 'Powered by'} className="h-5 object-contain" />
                                                                        ) : (
                                                                            <span className="text-sm">{footerPoweredByText}</span>
                                                                        )}
                                                                    </a>
                                                                ) : (
                                                                    <div className="inline-flex items-center gap-2 text-gray-600">
                                                                        {footerPoweredByLogoDarkUrl ? (
                                                                            <img src={footerPoweredByLogoDarkUrl} alt={footerPoweredByText || 'Powered by'} className="h-5 object-contain" />
                                                                        ) : (
                                                                            <span className="text-sm">{footerPoweredByText}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 p-4 bg-slate-800 rounded border border-slate-700">
                                                                <p className="text-xs text-gray-400 mb-2">Dark Background (Footer)</p>
                                                                {footerPoweredByLink ? (
                                                                    <a href={footerPoweredByLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                                                                        {footerPoweredByLogoLightUrl ? (
                                                                            <img src={footerPoweredByLogoLightUrl} alt={footerPoweredByText || 'Powered by'} className="h-5 object-contain" />
                                                                        ) : (
                                                                            <span className="text-sm">{footerPoweredByText}</span>
                                                                        )}
                                                                    </a>
                                                                ) : (
                                                                    <div className="inline-flex items-center gap-2 text-gray-300">
                                                                        {footerPoweredByLogoLightUrl ? (
                                                                            <img src={footerPoweredByLogoLightUrl} alt={footerPoweredByText || 'Powered by'} className="h-5 object-contain" />
                                                                        ) : (
                                                                            <span className="text-sm">{footerPoweredByText}</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Footer Typography */}
                                <div className="border-t pt-4">
                                    <h4 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Typography</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                        <div className="space-y-2">
                                            <Label>Font Family</Label>
                                            <select
                                                value={footerFont || ''}
                                                onChange={(e) => setFooterFont(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="">Inherit from body</option>
                                                {GOOGLE_FONTS.map(font => (
                                                    <option key={font.name} value={font.name}>{font.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Heading Size (px)</Label>
                                            <Input type="number" value={footerHeadingFontSize} onChange={(e) => setFooterHeadingFontSize(e.target.value)} min="12" max="24" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Heading Weight</Label>
                                            <select
                                                value={footerHeadingFontWeight}
                                                onChange={(e) => setFooterHeadingFontWeight(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="400">Regular (400)</option>
                                                <option value="500">Medium (500)</option>
                                                <option value="600">Semi-Bold (600)</option>
                                                <option value="700">Bold (700)</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Link Size (px)</Label>
                                            <Input type="number" value={footerLinkFontSize} onChange={(e) => setFooterLinkFontSize(e.target.value)} min="10" max="18" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Link Weight</Label>
                                            <select
                                                value={footerLinkFontWeight}
                                                onChange={(e) => setFooterLinkFontWeight(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-md bg-background text-sm text-[#222224]"
                                            >
                                                <option value="300">Light (300)</option>
                                                <option value="400">Regular (400)</option>
                                                <option value="500">Medium (500)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Footer Settings'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Mobile Menu Tab - Blocksy-inspired */}
                    <TabsContent value="mobile" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Menu className="h-5 w-5" />
                                    Mobile Menu Settings
                                </CardTitle>
                                <CardDescription>Configure the mobile navigation menu appearance and behavior (Blocksy-inspired)</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-8">
                                {/* Hamburger Icon Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Hamburger Icon</h4>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Animation Style</Label>
                                            <select
                                                value={mobileMenuHamburgerStyle}
                                                onChange={(e) => setMobileMenuHamburgerStyle(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="default">Default</option>
                                                <option value="spin">Spin</option>
                                                <option value="squeeze">Squeeze</option>
                                                <option value="elastic">Elastic</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Size (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuHamburgerSize}
                                                onChange={(e) => setMobileMenuHamburgerSize(e.target.value)}
                                                min="16"
                                                max="40"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Line Width (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuHamburgerLineWidth}
                                                onChange={(e) => setMobileMenuHamburgerLineWidth(e.target.value)}
                                                min="1"
                                                max="4"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Panel Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Menu Panel</h4>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Position</Label>
                                            <select
                                                value={mobileMenuPosition}
                                                onChange={(e) => setMobileMenuPosition(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="right">Slide from Right</option>
                                                <option value="left">Slide from Left</option>
                                                <option value="top">Slide from Top</option>
                                                <option value="fullscreen">Fullscreen</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Width (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuWidth}
                                                onChange={(e) => setMobileMenuWidth(e.target.value)}
                                                min="240"
                                                max="480"
                                                disabled={mobileMenuPosition === 'fullscreen' || mobileMenuPosition === 'top'}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Animation</Label>
                                            <select
                                                value={mobileMenuAnimation}
                                                onChange={(e) => setMobileMenuAnimation(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="slide">Slide</option>
                                                <option value="fade">Fade</option>
                                                <option value="push">Push</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Duration (ms)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuAnimationDuration}
                                                onChange={(e) => setMobileMenuAnimationDuration(e.target.value)}
                                                min="100"
                                                max="500"
                                                step="50"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Colors */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Colors</h4>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Background</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={mobileMenuBgColor}
                                                    onChange={(e) => setMobileMenuBgColor(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input value={mobileMenuBgColor} onChange={(e) => setMobileMenuBgColor(e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Text Color</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={mobileMenuTextColor}
                                                    onChange={(e) => setMobileMenuTextColor(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input value={mobileMenuTextColor} onChange={(e) => setMobileMenuTextColor(e.target.value)} />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Overlay Color</Label>
                                            <Input
                                                value={mobileMenuOverlayColor}
                                                onChange={(e) => setMobileMenuOverlayColor(e.target.value)}
                                                placeholder="rgba(0, 0, 0, 0.5)"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Divider Color</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={mobileMenuDividerColor}
                                                    onChange={(e) => setMobileMenuDividerColor(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input value={mobileMenuDividerColor} onChange={(e) => setMobileMenuDividerColor(e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Link Styling */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Link Styling</h4>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Link Style</Label>
                                            <select
                                                value={mobileMenuLinkStyle}
                                                onChange={(e) => setMobileMenuLinkStyle(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="default">Default (Simple)</option>
                                                <option value="bordered">Bordered</option>
                                                <option value="filled">Filled Background</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Size (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuLinkFontSize}
                                                onChange={(e) => setMobileMenuLinkFontSize(e.target.value)}
                                                min="12"
                                                max="24"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Font Weight</Label>
                                            <select
                                                value={mobileMenuLinkFontWeight}
                                                onChange={(e) => setMobileMenuLinkFontWeight(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="400">Regular (400)</option>
                                                <option value="500">Medium (500)</option>
                                                <option value="600">Semibold (600)</option>
                                                <option value="700">Bold (700)</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Spacing (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuLinkSpacing}
                                                onChange={(e) => setMobileMenuLinkSpacing(e.target.value)}
                                                min="8"
                                                max="32"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Button Layout */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Action Buttons</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Button Layout</Label>
                                            <select
                                                value={mobileMenuButtonLayout}
                                                onChange={(e) => setMobileMenuButtonLayout(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="stacked">Stacked (Vertical)</option>
                                                <option value="inline">Inline (Horizontal)</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Button Spacing (px)</Label>
                                            <Input
                                                type="number"
                                                value={mobileMenuButtonSpacing}
                                                onChange={(e) => setMobileMenuButtonSpacing(e.target.value)}
                                                min="4"
                                                max="24"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Toggle Options */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Display Options</h4>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={mobileMenuShowLogo}
                                                onChange={(e) => setMobileMenuShowLogo(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm">Show Logo</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={mobileMenuShowCloseButton}
                                                onChange={(e) => setMobileMenuShowCloseButton(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm">Show Close Button</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={mobileMenuShowDividers}
                                                onChange={(e) => setMobileMenuShowDividers(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm">Show Dividers</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={mobileMenuOverlayBlur}
                                                onChange={(e) => setMobileMenuOverlayBlur(e.target.checked)}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm">Blur Overlay</span>
                                        </label>
                                    </div>
                                    {mobileMenuShowCloseButton && (
                                        <div className="space-y-2 max-w-xs">
                                            <Label>Close Button Position</Label>
                                            <select
                                                value={mobileMenuCloseButtonPosition}
                                                onChange={(e) => setMobileMenuCloseButtonPosition(e.target.value)}
                                                className="w-full h-10 px-3 border rounded-md bg-background"
                                            >
                                                <option value="right">Right</option>
                                                <option value="left">Left</option>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {/* Footer Text */}
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">Footer</h4>
                                    <div className="space-y-2">
                                        <Label>Footer Text (optional)</Label>
                                        <Input
                                            value={mobileMenuFooterText}
                                            onChange={(e) => setMobileMenuFooterText(e.target.value)}
                                            placeholder="e.g., © 2024 Your Company"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Header Height (px)</Label>
                                        <Input
                                            type="number"
                                            value={mobileMenuHeaderHeight}
                                            onChange={(e) => setMobileMenuHeaderHeight(e.target.value)}
                                            min="48"
                                            max="96"
                                            className="max-w-xs"
                                        />
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Mobile Menu Settings'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Button Styles Tab */}
                    <TabsContent value="buttons" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Button Styles</CardTitle>
                                <CardDescription>Configure global button appearance across the site</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid lg:grid-cols-2 gap-8">
                                    {/* Primary Button */}
                                    <div className="space-y-4">
                                        <h4 className="font-medium">Primary Button</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Background</Label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="color"
                                                        value={buttonPrimaryBg}
                                                        onChange={(e) => setButtonPrimaryBg(e.target.value)}
                                                        className="w-10 h-10 rounded border cursor-pointer"
                                                    />
                                                    <Input
                                                        value={buttonPrimaryBg}
                                                        onChange={(e) => setButtonPrimaryBg(e.target.value)}
                                                        className="flex-1"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Text Color</Label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="color"
                                                        value={buttonPrimaryText}
                                                        onChange={(e) => setButtonPrimaryText(e.target.value)}
                                                        className="w-10 h-10 rounded border cursor-pointer"
                                                    />
                                                    <Input
                                                        value={buttonPrimaryText}
                                                        onChange={(e) => setButtonPrimaryText(e.target.value)}
                                                        className="flex-1"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Hover Background</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={buttonPrimaryHoverBg}
                                                    onChange={(e) => setButtonPrimaryHoverBg(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input
                                                    value={buttonPrimaryHoverBg}
                                                    onChange={(e) => setButtonPrimaryHoverBg(e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        {/* Preview */}
                                        <div className="pt-4">
                                            <button
                                                style={{
                                                    backgroundColor: buttonPrimaryBg,
                                                    color: buttonPrimaryText,
                                                    borderRadius: `${buttonBorderRadius}px`,
                                                    padding: `${buttonPaddingY}px ${buttonPaddingX}px`
                                                }}
                                                className="font-medium transition-colors"
                                            >
                                                Primary Button
                                            </button>
                                        </div>
                                    </div>

                                    {/* Secondary Button */}
                                    <div className="space-y-4">
                                        <h4 className="font-medium">Secondary Button</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Background</Label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="color"
                                                        value={buttonSecondaryBg}
                                                        onChange={(e) => setButtonSecondaryBg(e.target.value)}
                                                        className="w-10 h-10 rounded border cursor-pointer"
                                                    />
                                                    <Input
                                                        value={buttonSecondaryBg}
                                                        onChange={(e) => setButtonSecondaryBg(e.target.value)}
                                                        className="flex-1"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Text Color</Label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="color"
                                                        value={buttonSecondaryText}
                                                        onChange={(e) => setButtonSecondaryText(e.target.value)}
                                                        className="w-10 h-10 rounded border cursor-pointer"
                                                    />
                                                    <Input
                                                        value={buttonSecondaryText}
                                                        onChange={(e) => setButtonSecondaryText(e.target.value)}
                                                        className="flex-1"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Border Color</Label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="color"
                                                    value={buttonSecondaryBorder}
                                                    onChange={(e) => setButtonSecondaryBorder(e.target.value)}
                                                    className="w-10 h-10 rounded border cursor-pointer"
                                                />
                                                <Input
                                                    value={buttonSecondaryBorder}
                                                    onChange={(e) => setButtonSecondaryBorder(e.target.value)}
                                                    className="flex-1"
                                                />
                                            </div>
                                        </div>
                                        {/* Preview */}
                                        <div className="pt-4">
                                            <button
                                                style={{
                                                    backgroundColor: buttonSecondaryBg,
                                                    color: buttonSecondaryText,
                                                    border: `2px solid ${buttonSecondaryBorder}`,
                                                    borderRadius: `${buttonBorderRadius}px`,
                                                    padding: `${buttonPaddingY}px ${buttonPaddingX}px`
                                                }}
                                                className="font-medium transition-colors"
                                            >
                                                Secondary Button
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Shared Settings */}
                                <div className="pt-6 border-t">
                                    <h4 className="font-medium mb-4">Shared Settings</h4>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="space-y-2">
                                            <Label>Border Radius (px)</Label>
                                            <Input
                                                type="number"
                                                value={buttonBorderRadius}
                                                onChange={(e) => setButtonBorderRadius(e.target.value)}
                                                min="0"
                                                max="50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Horizontal Padding (px)</Label>
                                            <Input
                                                type="number"
                                                value={buttonPaddingX}
                                                onChange={(e) => setButtonPaddingX(e.target.value)}
                                                min="8"
                                                max="64"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Vertical Padding (px)</Label>
                                            <Input
                                                type="number"
                                                value={buttonPaddingY}
                                                onChange={(e) => setButtonPaddingY(e.target.value)}
                                                min="4"
                                                max="32"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Button Styles'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Conversion Widgets Tab */}
                    <TabsContent value="conversion" className="space-y-6">

                        <div className="grid lg:grid-cols-2 gap-8">
                            {/* Social Proof Settings */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="p-6 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Bell className="text-primary" size={24} />
                                            <h2 className="text-xl font-bold text-gray-900">Social Proof Notifications</h2>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={socialProofEnabled}
                                                onChange={(e) => setSocialProofEnabled(e.target.checked)}
                                                className="w-5 h-5 text-primary rounded focus:ring-primary"
                                            />
                                            <span className="text-sm font-medium">Enabled</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="p-6 space-y-6">
                                    {/* Timing Settings */}
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Timing Settings</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Initial Delay (seconds)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={socialProofInitialDelay}
                                                    onChange={(e) => setSocialProofInitialDelay(e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                    min="0"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Time before first notification appears</p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Repeat Interval (seconds)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={socialProofInterval}
                                                    onChange={(e) => setSocialProofInterval(e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                    min="0"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Time between notifications (180s = 3 minutes)</p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Display Duration (seconds)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={socialProofDuration}
                                                    onChange={(e) => setSocialProofDuration(e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                    min="1"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">How long each notification stays visible</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Notifications */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-sm font-semibold text-gray-900">Notification Messages</h3>
                                            <button
                                                onClick={addNotification}
                                                className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
                                            >
                                                <Plus size={16} />
                                                Add
                                            </button>
                                        </div>
                                        <div className="space-y-3 max-h-96 overflow-y-auto">
                                            {socialProofNotifications.map((notification) => (
                                                <div key={notification.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                                    <div className="flex items-start justify-between mb-2">
                                                        <span className="text-xs font-medium text-gray-500">Notification #{notification.id}</span>
                                                        <button
                                                            onClick={() => removeNotification(notification.id)}
                                                            className="text-red-500 hover:text-red-700"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={notification.message}
                                                        onChange={(e) => updateNotification(notification.id, 'message', e.target.value)}
                                                        placeholder="e.g., A consulting firm started their trial"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded mb-2 text-sm text-[#222224]"
                                                    />
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <input
                                                            type="text"
                                                            value={notification.location}
                                                            onChange={(e) => updateNotification(notification.id, 'location', e.target.value)}
                                                            placeholder="Location"
                                                            className="px-3 py-2 border border-gray-300 rounded text-sm text-[#222224]"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={notification.time}
                                                            onChange={(e) => updateNotification(notification.id, 'time', e.target.value)}
                                                            placeholder="Time ago"
                                                            className="px-3 py-2 border border-gray-300 rounded text-sm text-[#222224]"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Exit Intent Settings */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="p-6 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <XIcon className="text-primary" size={24} />
                                            <h2 className="text-xl font-bold text-gray-900">Exit Intent Popup</h2>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={exitIntentEnabled}
                                                onChange={(e) => setExitIntentEnabled(e.target.checked)}
                                                className="w-5 h-5 text-primary rounded focus:ring-primary"
                                            />
                                            <span className="text-sm font-medium">Enabled</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="p-6 space-y-6">
                                    {/* Timing Settings */}
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Timing Settings</h3>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Cooldown Period (hours)
                                            </label>
                                            <input
                                                type="number"
                                                value={exitIntentCooldown}
                                                onChange={(e) => setExitIntentCooldown(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                min="1"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Time before showing popup again to same user</p>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Content</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Title
                                                </label>
                                                <input
                                                    type="text"
                                                    value={exitIntentTitle}
                                                    onChange={(e) => setExitIntentTitle(e.target.value)}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Subtitle
                                                </label>
                                                <textarea
                                                    value={exitIntentSubtitle}
                                                    onChange={(e) => setExitIntentSubtitle(e.target.value)}
                                                    rows={3}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-[#222224]"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Benefits */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-sm font-semibold text-gray-900">Benefits List</h3>
                                            <button
                                                onClick={addBenefit}
                                                className="flex items-center gap-1 text-sm text-primary hover:text-primary-hover"
                                            >
                                                <Plus size={16} />
                                                Add
                                            </button>
                                        </div>
                                        <div className="space-y-2">
                                            {exitIntentBenefits.map((benefit, index) => (
                                                <div key={index} className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={benefit}
                                                        onChange={(e) => updateBenefit(index, e.target.value)}
                                                        placeholder="Enter benefit"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm text-[#222224]"
                                                    />
                                                    <button
                                                        onClick={() => removeBenefit(index)}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end pt-4">
                            <Button onClick={handleSave} disabled={saving}>
                                <Save className="h-4 w-4 mr-2" />
                                {saving ? 'Saving...' : 'Save Conversion Settings'}
                            </Button>
                        </div>
                    </TabsContent>

                    {/* Contact Information Tab */}
                    <TabsContent value="contact" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Contact Information</CardTitle>
                                <CardDescription>Configure your business contact details displayed on the website</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Email */}
                                <div className="space-y-2">
                                    <Label htmlFor="contactEmail">Email Address</Label>
                                    <Input
                                        id="contactEmail"
                                        type="email"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        placeholder="contact@example.com"
                                    />
                                </div>

                                {/* Phone Numbers */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label>Phone Numbers</Label>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setContactPhones([...contactPhones, { label: '', number: '' }])}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Phone
                                        </Button>
                                    </div>
                                    {contactPhones.map((phone, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <Input
                                                value={phone.label}
                                                onChange={(e) => {
                                                    const updated = [...contactPhones];
                                                    updated[index].label = e.target.value;
                                                    setContactPhones(updated);
                                                }}
                                                placeholder="Label (e.g., ZA, ZM)"
                                                className="w-24"
                                            />
                                            <Input
                                                value={phone.number}
                                                onChange={(e) => {
                                                    const updated = [...contactPhones];
                                                    updated[index].number = e.target.value;
                                                    setContactPhones(updated);
                                                }}
                                                placeholder="+27 00 000 0000"
                                                className="flex-1"
                                            />
                                            {contactPhones.length > 1 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setContactPhones(contactPhones.filter((_, i) => i !== index));
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Address */}
                                <div className="space-y-4">
                                    <Label className="text-base font-semibold">Physical Address</Label>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressStreet">Street Address</Label>
                                            <Input
                                                id="contactAddressStreet"
                                                value={contactAddressStreet}
                                                onChange={(e) => setContactAddressStreet(e.target.value)}
                                                placeholder="123 Main Street"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressArea">Area/Suburb</Label>
                                            <Input
                                                id="contactAddressArea"
                                                value={contactAddressArea}
                                                onChange={(e) => setContactAddressArea(e.target.value)}
                                                placeholder="Suburb name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressCity">City</Label>
                                            <Input
                                                id="contactAddressCity"
                                                value={contactAddressCity}
                                                onChange={(e) => setContactAddressCity(e.target.value)}
                                                placeholder="City"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressProvince">Province/State</Label>
                                            <Input
                                                id="contactAddressProvince"
                                                value={contactAddressProvince}
                                                onChange={(e) => setContactAddressProvince(e.target.value)}
                                                placeholder="Province"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressCountry">Country</Label>
                                            <Input
                                                id="contactAddressCountry"
                                                value={contactAddressCountry}
                                                onChange={(e) => setContactAddressCountry(e.target.value)}
                                                placeholder="Country"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contactAddressPostalCode">Postal Code</Label>
                                            <Input
                                                id="contactAddressPostalCode"
                                                value={contactAddressPostalCode}
                                                onChange={(e) => setContactAddressPostalCode(e.target.value)}
                                                placeholder="0000"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Preview */}
                                <div className="border rounded-lg p-4 bg-muted/50">
                                    <Label className="text-sm font-medium mb-3 block">Preview</Label>
                                    <div className="space-y-2 text-sm">
                                        <p><strong>Email:</strong> {contactEmail}</p>
                                        <p><strong>Phone:</strong> {contactPhones.map(p => `${p.label ? `(${p.label}) ` : ''}${p.number}`).join(' | ')}</p>
                                        <p><strong>Address:</strong> {contactAddressStreet}, {contactAddressArea}, {contactAddressCity}, {contactAddressProvince}, {contactAddressCountry} {contactAddressPostalCode}</p>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4 border-t">
                                    <Button onClick={saveBrandSettings} disabled={saving}>
                                        <Save className="h-4 w-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Contact Information'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default CMSSettingsPage;

